import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { requestIdleCallback, viewTransition } from '@theme/utilities';
import { ThemeEvents } from '@theme/events';
import { PaginatedListAspectRatioHelper } from '@theme/paginated-list-aspect-ratio';
import { morph } from '@theme/morph';

/**
 * Returns a stable browser URL while preserving its fragment.
 * @param {URL | string} url
 * @returns {URL}
 */
export function normalizeHistoryURL(url) {
  const normalizedURL = new URL(url.toString(), window.location.origin);
  normalizedURL.searchParams.delete('section_id');
  normalizedURL.searchParams.sort();
  return normalizedURL;
}

/**
 * Returns the URL sent to the server. Fragments are browser-only.
 * @param {URL | string} url
 * @returns {URL}
 */
export function normalizeRequestURL(url) {
  const normalizedURL = normalizeHistoryURL(url);
  normalizedURL.hash = '';
  return normalizedURL;
}

/**
 * @param {URL | string} url
 * @returns {string}
 */
export function getPageCacheKey(url) {
  return normalizeRequestURL(url).toString();
}

/**
 * Returns the URL context shared by every page in one paginated result set.
 * @param {URL | string} url
 * @returns {string}
 */
function getPageContextKey(url) {
  const contextURL = normalizeRequestURL(url);
  contextURL.searchParams.delete('page');
  return contextURL.toString();
}

/**
 * @param {unknown} error
 * @returns {boolean}
 */
function isAbortError(error) {
  return typeof error === 'object' && error !== null && 'name' in error && error.name === 'AbortError';
}

/**
 * @typedef {object} BrowserEntryMetadata
 * @property {string} [owner]
 * @property {{ title: string, canonical: string }} [pageMetadata]
 * @property {string} [urlParameters]
 *
 * @typedef {object} BrowserEntryRecord
 * @property {symbol} entryId
 * @property {number} revision
 * @property {number} selection
 * @property {string | undefined} navigationKey
 * @property {number | undefined} navigationIndex
 * @property {BrowserEntryMetadata | undefined} metadata
 *
 * @typedef {object} BrowserEntryAuthority
 * @property {symbol} entryId
 * @property {number} revision
 * @property {number} selection
 * @property {string | undefined} navigationKey
 * @property {number | undefined} navigationIndex
 */

/** @type {Map<string, BrowserEntryRecord>} */
const browserEntryRecordsByNavigationKey = new Map();
let browserEntrySequence = 0;
let browserEntrySelection = 0;

/**
 * @returns {{ key: string | undefined, index: number | undefined }}
 */
function getNavigationEntryIdentity() {
  const entry = /** @type {{ currentEntry?: { key?: unknown, index?: unknown } }} */ (
    /** @type {unknown} */ (window.navigation)
  )?.currentEntry;
  return {
    key: typeof entry?.key === 'string' ? entry.key : undefined,
    index: Number.isInteger(entry?.index) ? Number(entry.index) : undefined,
  };
}

/**
 * @param {Partial<BrowserEntryRecord>} [values]
 * @returns {BrowserEntryRecord}
 */
function createBrowserEntryRecord(values = {}) {
  const navigation = getNavigationEntryIdentity();
  return {
    entryId: values.entryId ?? Symbol(`browser-entry:${++browserEntrySequence}`),
    revision: values.revision ?? 0,
    selection: ++browserEntrySelection,
    navigationKey: navigation.key,
    navigationIndex: navigation.index,
    metadata: values.metadata,
  };
}

/** @type {BrowserEntryRecord} */
let currentBrowserEntryRecord = createBrowserEntryRecord();
if (currentBrowserEntryRecord.navigationKey) {
  browserEntryRecordsByNavigationKey.set(
    currentBrowserEntryRecord.navigationKey,
    currentBrowserEntryRecord
  );
}

/**
 * @returns {BrowserEntryAuthority}
 */
function captureBrowserEntryAuthority() {
  const navigation = getNavigationEntryIdentity();
  if (
    navigation.key !== currentBrowserEntryRecord.navigationKey ||
    navigation.index !== currentBrowserEntryRecord.navigationIndex
  ) {
    const knownRecord = navigation.key
      ? browserEntryRecordsByNavigationKey.get(navigation.key)
      : undefined;
    currentBrowserEntryRecord = createBrowserEntryRecord(
      knownRecord
        ? {
            entryId: knownRecord.entryId,
            revision: knownRecord.revision,
            metadata: knownRecord.metadata,
          }
        : undefined
    );
    if (navigation.key) {
      browserEntryRecordsByNavigationKey.set(navigation.key, currentBrowserEntryRecord);
    }
  }

  return Object.freeze({
    entryId: currentBrowserEntryRecord.entryId,
    revision: currentBrowserEntryRecord.revision,
    selection: currentBrowserEntryRecord.selection,
    navigationKey: currentBrowserEntryRecord.navigationKey,
    navigationIndex: currentBrowserEntryRecord.navigationIndex,
  });
}

/**
 * @param {BrowserEntryAuthority} left
 * @param {BrowserEntryAuthority} right
 * @returns {boolean}
 */
function isSameBrowserEntryAuthority(left, right) {
  return (
    left.entryId === right.entryId &&
    left.revision === right.revision &&
    left.navigationKey === right.navigationKey &&
    left.navigationIndex === right.navigationIndex
  );
}

/**
 * @param {'push' | 'replace' | 'pop'} mutation
 */
function recordBrowserEntryMutation(mutation) {
  const navigation = getNavigationEntryIdentity();
  const knownRecord = navigation.key
    ? browserEntryRecordsByNavigationKey.get(navigation.key)
    : undefined;

  if (mutation === 'push') {
    currentBrowserEntryRecord = createBrowserEntryRecord();
  } else if (mutation === 'replace') {
    currentBrowserEntryRecord = createBrowserEntryRecord({
      entryId: currentBrowserEntryRecord.entryId,
      revision: currentBrowserEntryRecord.revision + 1,
      metadata: currentBrowserEntryRecord.metadata,
    });
  } else {
    currentBrowserEntryRecord = createBrowserEntryRecord(
      knownRecord
        ? {
            entryId: knownRecord.entryId,
            revision: knownRecord.revision,
            metadata: knownRecord.metadata,
          }
        : undefined
    );
  }

  if (navigation.key) {
    browserEntryRecordsByNavigationKey.set(navigation.key, currentBrowserEntryRecord);
  }
}

/**
 * @param {BrowserEntryMetadata | undefined} metadata
 */
function setCurrentBrowserEntryMetadata(metadata) {
  currentBrowserEntryRecord.metadata = metadata
    ? {
        ...metadata,
        pageMetadata: metadata.pageMetadata ? { ...metadata.pageMetadata } : undefined,
      }
    : undefined;
  if (currentBrowserEntryRecord.navigationKey) {
    browserEntryRecordsByNavigationKey.set(
      currentBrowserEntryRecord.navigationKey,
      currentBrowserEntryRecord
    );
  }
}

/**
 * @returns {BrowserEntryMetadata | undefined}
 */
function getCurrentBrowserEntryMetadata() {
  const metadata = currentBrowserEntryRecord.metadata;
  return metadata
    ? {
        ...metadata,
        pageMetadata: metadata.pageMetadata ? { ...metadata.pageMetadata } : undefined,
      }
    : undefined;
}

/**
 * @param {PopStateEvent} event
 * @returns {{ title: string, canonical: string } | undefined}
 */
function getPopStatePageMetadata(event) {
  const metadata = getCurrentBrowserEntryMetadata()?.pageMetadata ?? event.state?.pageMetadata;
  return metadata &&
    typeof metadata.title === 'string' &&
    typeof metadata.canonical === 'string'
    ? { title: metadata.title, canonical: metadata.canonical }
    : undefined;
}

const nativeHistoryPushState = history.pushState;
const nativeHistoryReplaceState = history.replaceState;

history.pushState = function pushState(state, unused, url) {
  const result = nativeHistoryPushState.call(this, state, unused, url);
  recordBrowserEntryMutation('push');
  return result;
};

history.replaceState = function replaceState(state, unused, url) {
  const result = nativeHistoryReplaceState.call(this, state, unused, url);
  recordBrowserEntryMutation('replace');
  return result;
};

/**
 * Applies metadata calculated by the server for a paginated page.
 * @param {{ title: string, canonical: string } | undefined} metadata
 */
function applyPageMetadata(metadata) {
  if (!metadata || typeof metadata.title !== 'string' || typeof metadata.canonical !== 'string') return;

  document.title = metadata.title;

  const canonicalLink = document.querySelector('link[rel="canonical"]');
  if (canonicalLink instanceof HTMLLinkElement) {
    canonicalLink.href = metadata.canonical;
  }
}

/** @type {Set<PaginatedList>} */
const connectedPaginatedLists = new Set();

const PAGINATION_HISTORY_OWNER = 'paginatedListSectionId';
const FACET_RENDER_SETTLED_EVENT = 'facet:section-render-settled';
const FACET_DOM_MUTATION_EVENT = 'facet:section-dom-mutation';
export const FACET_RENDER_SUPERSEDE_EVENT = 'facet:section-render-supersede';
const HISTORY_POLICY_WRITE = 'write';
const HISTORY_POLICY_NONE = 'none';

/**
 * @typedef {object} SelectedEntry
 * @property {number} navigationEpoch
 * @property {string} url
 * @property {unknown} state
 * @property {BrowserEntryAuthority} browserEntry
 * @property {string} requestKey
 *
 * @typedef {object} RenderedCommit
 * @property {string} requestKey
 * @property {number} generation
 * @property {number} commitId
 * @property {{ title: string, canonical: string } | undefined} [metadata]
 *
 * @typedef {object} LiveDOMCommit
 * @property {string} requestKey
 * @property {number} generation
 * @property {number} commitId
 * @property {'committed' | 'dirty'} state
 * @property {string} [mutationToken]
 *
 * @typedef {object} RollbackCapsule
 * @property {string} rootId
 * @property {string} sectionHTML
 * @property {RenderedCommit} renderedCommit
 * @property {LiveDOMCommit} liveDOMCommit
 * @property {{ title: string, canonical: string } | undefined} [committedMetadata]
 * @property {string} title
 * @property {string | undefined} canonical
 *
 * @typedef {object} DOMMutationLease
 * @property {string} id
 * @property {string} ownerToken
 * @property {number} navigationEpoch
 * @property {'clean' | 'dirty' | 'committed' | 'rolled-back' | 'invalid'} state
 * @property {RollbackCapsule | undefined} rollbackCapsule
 * @property {string} [mutationToken]
 *
 * @typedef {object} SectionRenderAuthoritySnapshot
 * @property {string} sectionId
 * @property {string} transactionId
 * @property {number} generation
 * @property {number} navigationEpoch
 * @property {string} selectedURL
 * @property {BrowserEntryAuthority} browserEntry
 * @property {string} requestKey
 * @property {string} targetURL
 * @property {'filter' | 'popstate'} origin
 * @property {'write' | 'none'} historyPolicy
 * @property {symbol | undefined} instanceToken
 * @property {string | undefined} domMutationLeaseId
 *
 * @typedef {object} SectionRenderAuthority
 * @property {SectionRenderAuthoritySnapshot} snapshot
 * @property {'pending' | 'success' | 'failure' | 'superseded'} settlement
 */

let browserNavigationEpoch = 0;

/** @type {Map<string, SectionRenderAuthority>} */
const sectionRenderAuthorities = new Map();

/**
 * Creates the shared section-scoped authority used by Facets and PaginatedList.
 * @param {{
 *   sectionId: string,
 *   token: string,
 *   requestURL: string,
 *   origin: 'filter' | 'popstate',
 *   historyPolicy: 'write' | 'none'
 * }} transaction
 * @param {{
 *   generation?: number,
 *   selectedEntry?: SelectedEntry,
 *   instanceToken?: symbol,
 *   domMutationLeaseId?: string
 * }} [options]
 * @returns {SectionRenderAuthority}
 */
export function createSectionRenderAuthority(transaction, options = {}) {
  const previousAuthority = sectionRenderAuthorities.get(transaction.sectionId);
  if (previousAuthority?.settlement === 'pending') previousAuthority.settlement = 'superseded';

  const selectedEntry =
    options.selectedEntry ??
    {
      navigationEpoch: browserNavigationEpoch,
      url: window.location.href,
      state: history.state,
      browserEntry: captureBrowserEntryAuthority(),
      requestKey: getPageCacheKey(window.location.href),
    };
  const snapshot = Object.freeze({
    sectionId: transaction.sectionId,
    transactionId: transaction.token,
    generation: options.generation ?? 0,
    navigationEpoch: selectedEntry.navigationEpoch,
    selectedURL: selectedEntry.url,
    browserEntry: selectedEntry.browserEntry,
    requestKey: transaction.requestURL,
    targetURL:
      typeof transaction.historyURL === 'string'
        ? normalizeHistoryURL(transaction.historyURL).toString()
        : selectedEntry.url,
    origin: transaction.origin,
    historyPolicy: transaction.historyPolicy,
    instanceToken: options.instanceToken,
    domMutationLeaseId: options.domMutationLeaseId,
  });
  const authority = {
    snapshot,
    settlement: /** @type {'pending'} */ ('pending'),
  };

  sectionRenderAuthorities.set(transaction.sectionId, authority);
  return authority;
}

/**
 * Adopts only a duplicate signal for the exact immutable render/browser-entry authority.
 * @param {SectionRenderAuthority} authority
 * @param {SelectedEntry} selectedEntry
 * @returns {boolean}
 */
function adoptSectionRenderAuthority(authority, selectedEntry) {
  const snapshot = authority.snapshot;
  if (
    sectionRenderAuthorities.get(snapshot.sectionId) !== authority ||
    authority.settlement !== 'pending' ||
    snapshot.navigationEpoch !== selectedEntry.navigationEpoch ||
    snapshot.selectedURL !== selectedEntry.url ||
    snapshot.requestKey !== selectedEntry.requestKey ||
    !isSameBrowserEntryAuthority(snapshot.browserEntry, selectedEntry.browserEntry)
  ) {
    return false;
  }

  return true;
}

/**
 * @param {SectionRenderAuthority | undefined} authority
 * @returns {boolean}
 */
export function isSectionRenderAuthorityCurrent(authority) {
  const snapshot = authority?.snapshot;
  const currentBrowserEntry = captureBrowserEntryAuthority();
  return Boolean(
    authority &&
      snapshot &&
      sectionRenderAuthorities.get(snapshot.sectionId) === authority &&
      snapshot.navigationEpoch === browserNavigationEpoch &&
      snapshot.selectedURL === window.location.href &&
      isSameBrowserEntryAuthority(snapshot.browserEntry, currentBrowserEntry) &&
      (authority.settlement === 'pending' || authority.settlement === 'success')
  );
}

/**
 * @param {SectionRenderAuthority | undefined} authority
 * @param {'success' | 'failure' | 'superseded'} settlement
 */
export function settleSectionRenderAuthority(authority, settlement) {
  if (!authority || authority.settlement !== 'pending') return;
  authority.settlement = settlement;
}

/**
 * @param {SectionRenderAuthority | undefined} authority
 */
function supersedeSectionRenderAuthority(authority) {
  if (!authority) return;
  authority.settlement = 'superseded';
}

window.addEventListener('popstate', (event) => {
  recordBrowserEntryMutation('pop');
  const navigationEpoch = ++browserNavigationEpoch;
  const connectedLists = [...connectedPaginatedLists].filter((list) => list.isConnected);
  const entryMetadata = getCurrentBrowserEntryMetadata();
  const historyOwner =
    typeof entryMetadata?.owner === 'string'
      ? entryMetadata.owner
      : typeof event.state?.[PAGINATION_HISTORY_OWNER] === 'string'
        ? event.state[PAGINATION_HISTORY_OWNER]
      : undefined;
  const owner =
    connectedLists.find((list) => list.sectionId === historyOwner) ??
    connectedLists.find((list) => list.localName === 'results-list') ??
    connectedLists[0];

  if (owner) {
    owner.restoreHistoryState(event, navigationEpoch);
    for (const list of connectedLists) {
      if (list !== owner && list.sectionId !== owner.sectionId) {
        list.suppressHistoryForNavigation(event, navigationEpoch);
      }
    }
  } else {
    applyPageMetadata(entryMetadata?.pageMetadata ?? getPopStatePageMetadata(event));
  }
});

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLElement} [viewMorePrevious] - The previous page marker or fallback link.
 * @property {HTMLElement} [viewMoreNext] - The next page marker or fallback link.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @typedef {object} PageInfo
 * @property {number} page
 * @property {URL} historyURL
 * @property {URL} requestURL
 * @property {string} cacheKey
 * @property {string} contextKey
 *
 * @typedef {object} ObserverRetryAuthority
 * @property {Element} boundary
 * @property {'previous' | 'next'} direction
 * @property {string} requestKey
 * @property {string} contextKey
 * @property {number} observerEpoch
 * @property {number} lifecycleGeneration
 * @property {string} contextId
 * @property {number} navigationEpoch
 * @property {SelectedEntry} selectedEntry
 * @property {{ selectedEntry: SelectedEntry, navigationEpoch: number, requestKey: string }} failedTarget
 * @property {boolean} initialDeliveryConsumed
 * @property {boolean | undefined} isIntersecting
 * @property {boolean} accepted
 *
 * @typedef {object} ObserverOwner
 * @property {symbol} token
 * @property {number} epoch
 * @property {string} contextId
 * @property {ObserverRetryAuthority | undefined} retryAuthority
 *
 * @typedef {object} ServerRenderTransaction
 * @property {string} token
 * @property {string} sectionId
 * @property {string} requestURL
 * @property {'filter' | 'popstate'} origin
 * @property {'write' | 'none'} historyPolicy
 * @property {'cache' | 'network'} source
 * @property {string} [historyURL]
 * @property {string} [urlParameters]
 * @property {boolean} [publishHistory]
 * @property {boolean} [historyPublished]
 * @property {number} generation
 * @property {boolean} committed
 * @property {boolean} [morphed]
 * @property {'pending' | 'success' | 'failure' | 'superseded'} settlement
 * @property {SectionRenderAuthority} authority
 * @property {boolean} [facetOwned]
 * @property {{ id: string, origin: LoadContext['origin'], historyPolicy: 'write' | 'none' }} recoveryContext
 * @property {RollbackCapsule | undefined} rollbackCapsule
 * @property {DOMMutationLease} domMutationLease
 * @property {SelectedEntry} sourceEntry
 * @property {SelectedEntry} targetEntry
 * @property {SelectedEntry} [committedEntry]
 * @property {ReturnType<typeof sectionRenderer.createSectionRender>} [preparedRender]
 *
 * @typedef {object} LoadContext
 * @property {string} id
 * @property {number} generation
 * @property {number} lifecycleGeneration
 * @property {'connect' | 'reconnect' | 'filter' | 'popstate' | 'scroll' | 'recovery'} origin
 * @property {'write' | 'none'} historyPolicy
 * @property {number} pendingPageTasks
 * @property {number} pendingInitialDeliveries
 * @property {number} pendingLifecycleCallbacks
 * @property {number} runningLifecycleCallbacks
 * @property {number} pendingObserverCallbacks
 * @property {number} runningObserverCallbacks
 * @property {number} activityEpoch
 * @property {number} quiescenceProbe
 * @property {boolean} immediateIntersection
 * @property {Set<Element>} intersectingTargets
 * @property {boolean} quiescent
 * @property {boolean} active
 * @property {number} navigationEpoch
 * @property {SelectedEntry} selectedEntry
 * @property {number} sourceCommitId
 * @property {string} sourceRequestKey
 * @property {number} sourceDOMGeneration
 * @property {'active' | 'draining' | 'quiescent' | 'superseded'} state
 * @property {'pending' | 'success' | 'superseded'} settlement
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  #instanceToken = Symbol('paginated-list-instance');

  /** @type {Map<string, string>} */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {ObserverOwner | undefined} */
  #observerOwner;

  /** @type {(() => void) | undefined} */
  #observerInitialRelease;

  #observerEpoch = 0;

  /** @type {Map<number, { cancel: () => void }>} */
  #scheduledObserverCallbacks = new Map();

  /** @type {Map<string, { generation: number, contextId: string, promise: Promise<boolean> }>} */
  #pendingPageRequests = new Map();

  /** @type {Set<string>} */
  #renderingPageKeys = new Set();

  #requestGeneration = 0;

  /** @type {PaginatedListAspectRatioHelper | undefined} */
  #aspectRatioHelper;

  #lifecycleGeneration = 0;

  /** @type {Map<number, { cancel: () => void }>} */
  #scheduledCallbacks = new Map();

  /** @type {ServerRenderTransaction | undefined} */
  #activeServerRenderTransaction;

  /** @type {LoadContext | undefined} */
  #activeLoadContext;

  /** @type {{ title: string, canonical: string } | undefined} */
  #committedMetadata;

  /** @type {SelectedEntry} */
  #selectedEntry = {
    navigationEpoch: browserNavigationEpoch,
    url: window.location.href,
    state: history.state,
    browserEntry: captureBrowserEntryAuthority(),
    requestKey: getPageCacheKey(window.location.href),
  };

  /** @type {RenderedCommit} */
  #renderedCommit = {
    requestKey: '',
    generation: 0,
    commitId: 0,
  };

  /** @type {LiveDOMCommit} */
  #liveDOMCommit = {
    requestKey: '',
    generation: 0,
    commitId: 0,
    state: 'committed',
  };

  /** @type {{ transaction: ServerRenderTransaction, selectedEntry: SelectedEntry, stagedMetadata?: { title: string, canonical: string } } | undefined} */
  #pendingNavigation;

  /** @type {{ selectedEntry: SelectedEntry, navigationEpoch: number, requestKey: string } | undefined} */
  #failedTarget;

  /** @type {RollbackCapsule | undefined} */
  #rollbackCapsule;

  #navigationEpoch = browserNavigationEpoch;

  #historyRenderSequence = 0;

  #loadContextSequence = 0;

  #domCommitSequence = 0;

  #hasConnected = false;

  connectedCallback() {
    super.connectedCallback();
    this.#lifecycleGeneration += 1;
    this.#navigationEpoch = browserNavigationEpoch;
    this.#selectedEntry = this.#captureSelectedEntry(history.state, this.#navigationEpoch);
    connectedPaginatedLists.add(this);

    const reconnect = this.#hasConnected;
    this.#hasConnected = true;
    this.#initializeRenderedState({
      cause: {
        id: `${this.sectionId}:${reconnect ? 'reconnect' : 'connect'}:${++this.#loadContextSequence}`,
        origin: reconnect ? 'reconnect' : 'connect',
        historyPolicy: reconnect ? HISTORY_POLICY_NONE : HISTORY_POLICY_WRITE,
      },
      replaceHistory: !reconnect,
      preserveRenderedCommit:
        reconnect &&
        (this.#liveDOMCommit.state === 'dirty' ||
          Boolean(this.#activeServerRenderTransaction) ||
          this.#hasDivergenceBarrier()),
    });
    document.addEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    document.addEventListener(FACET_RENDER_SETTLED_EVENT, this.#handleServerRenderSettled);
    document.addEventListener(FACET_DOM_MUTATION_EVENT, this.#handleServerDomMutation);
  }

  updatedCallback() {
    super.updatedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    connectedPaginatedLists.delete(this);
    this.#lifecycleGeneration += 1;
    this.#cancelScheduledCallbacks();
    this.#invalidateRequestGeneration();
    this.#disconnectInfinityScrollObserver();
    this.#cancelScheduledObserverCallbacks();
    this.#activeServerRenderTransaction = undefined;
    this.#pendingNavigation = undefined;
    if (this.#activeLoadContext) this.#supersedeLoadContext(this.#activeLoadContext);
    this.#activeLoadContext = undefined;
    document.removeEventListener(ThemeEvents.FilterUpdate, this.#handleFilterUpdate);
    document.removeEventListener(FACET_RENDER_SETTLED_EVENT, this.#handleServerRenderSettled);
    document.removeEventListener(FACET_DOM_MUTATION_EVENT, this.#handleServerDomMutation);
  }

  /**
   * @param {Promise<unknown> | (() => Promise<unknown>)} task
   * @param {LoadContext | undefined} context
   */
  #startPageTask(task, context = this.#activeLoadContext) {
    const tracksContext = context && this.#isLoadContextCurrent(context);
    if (tracksContext) {
      this.#markContextActivity(context);
      context.pendingPageTasks += 1;
    }

    let taskPromise;
    try {
      taskPromise = typeof task === 'function' ? task() : task;
    } catch (error) {
      taskPromise = Promise.reject(error);
    }

    void taskPromise
      .catch((error) => {
        if (!isAbortError(error) && (!tracksContext || this.#isLoadContextCurrent(context))) {
          console.error(error);
        }
      })
      .finally(() => {
        if (!tracksContext) return;
        context.pendingPageTasks = Math.max(0, context.pendingPageTasks - 1);
        context.activityEpoch += 1;
        this.#settleLoadContext(context);
      });
  }

  /**
   * Invalidates any in-flight quiescence probe before owned work crosses an async boundary.
   * @param {LoadContext} context
   */
  #markContextActivity(context) {
    context.activityEpoch += 1;
    context.quiescenceProbe += 1;
    context.quiescent = false;
    if (context.state !== 'superseded') context.state = 'draining';
    context.settlement = 'pending';
  }

  /**
   * @param {LoadContext} context
   */
  #supersedeLoadContext(context) {
    context.activityEpoch += 1;
    context.quiescenceProbe += 1;
    context.quiescent = false;
    context.active = false;
    context.state = 'superseded';
    context.settlement = 'superseded';
  }

  /**
   * @param {{
   *   cause?: { id: string, origin: LoadContext['origin'], historyPolicy: 'write' | 'none' },
   *   replaceHistory?: boolean,
   *   preserveRenderedCommit?: boolean,
   *   renderedTarget?: {
   *     selectedEntry: SelectedEntry,
   *     requestKey: string,
   *     metadata?: { title: string, canonical: string }
   *   }
   * }} [options]
   */
  #initializeRenderedState(options = {}) {
    if (!this.isConnected) return;

    const {
      cause = {
        id: `${this.sectionId}:load:${++this.#loadContextSequence}`,
        origin: /** @type {'connect'} */ ('connect'),
        historyPolicy: HISTORY_POLICY_WRITE,
      },
      replaceHistory = false,
      preserveRenderedCommit = false,
      renderedTarget,
    } = options;

    this.#cancelScheduledCallbacks();
    this.#disconnectInfinityScrollObserver();

    const generation = this.#invalidateRequestGeneration();
    const context = this.#createLoadContext(cause, generation);

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    this.#aspectRatioHelper = templateCard
      ? new PaginatedListAspectRatioHelper({
          templateCard,
        })
      : undefined;

    this.#startPageTask(() => this.#fetchPage('next', context), context);
    this.#startPageTask(() => this.#fetchPage('previous', context), context);
    this.#queueViewMoreObservation(context, false);
    if (!preserveRenderedCommit) {
      this.#syncCurrentPageMetadata(context, replaceHistory, renderedTarget);
    }
  }

  /**
   * @param {LoadContext} context
   * @param {() => void} callback
   * @param {() => void} [onCancel]
   */
  #scheduleLifecycleCallback(context, callback, onCancel) {
    if (!this.#isLoadContextCurrent(context)) {
      onCancel?.();
      return;
    }

    const lifecycleGeneration = this.#lifecycleGeneration;
    /** @type {number | undefined} */
    let callbackId;
    let released = false;

    this.#markContextActivity(context);
    context.pendingLifecycleCallbacks += 1;

    const releasePending = () => {
      if (released) return false;
      released = true;
      context.pendingLifecycleCallbacks = Math.max(0, context.pendingLifecycleCallbacks - 1);
      context.activityEpoch += 1;
      return true;
    };

    const cancel = () => {
      if (!releasePending()) return;
      if (callbackId !== undefined) {
        if (
          typeof window.requestIdleCallback === 'function' &&
          typeof window.cancelIdleCallback === 'function'
        ) {
          window.cancelIdleCallback(callbackId);
        } else {
          clearTimeout(callbackId);
        }
        this.#scheduledCallbacks.delete(callbackId);
      }
      onCancel?.();
      this.#settleLoadContext(context);
    };

    callbackId = requestIdleCallback(() => {
      if (callbackId !== undefined) this.#scheduledCallbacks.delete(callbackId);
      if (!releasePending()) return;
      context.runningLifecycleCallbacks += 1;
      this.#markContextActivity(context);

      try {
        if (
          !this.isConnected ||
          lifecycleGeneration !== this.#lifecycleGeneration ||
          !this.#isLoadContextCurrent(context)
        ) {
          onCancel?.();
          return;
        }
        callback();
      } finally {
        context.runningLifecycleCallbacks = Math.max(0, context.runningLifecycleCallbacks - 1);
        context.activityEpoch += 1;
        this.#settleLoadContext(context);
      }
    });

    if (callbackId !== undefined) this.#scheduledCallbacks.set(callbackId, { cancel });
    if (!this.#isLoadContextCurrent(context)) {
      cancel();
    }
  }

  #cancelScheduledCallbacks() {
    for (const { cancel } of [...this.#scheduledCallbacks.values()]) {
      cancel();
    }
    this.#scheduledCallbacks.clear();
  }

  /**
   * Schedules observer lifecycle work that is invalidated before an observer is replaced.
   * @param {number} observerEpoch
   * @param {LoadContext} context
   * @param {() => void} callback
   * @param {() => void} [onCancel]
   */
  #scheduleObserverCallback(observerEpoch, context, callback, onCancel) {
    if (!this.#isLoadContextCurrent(context)) {
      onCancel?.();
      return;
    }

    const lifecycleGeneration = this.#lifecycleGeneration;
    /** @type {number | undefined} */
    let callbackId;
    let released = false;

    this.#markContextActivity(context);
    context.pendingObserverCallbacks += 1;

    const releasePending = () => {
      if (released) return false;
      released = true;
      context.pendingObserverCallbacks = Math.max(0, context.pendingObserverCallbacks - 1);
      context.activityEpoch += 1;
      return true;
    };

    const cancel = () => {
      if (!releasePending()) return;
      if (callbackId !== undefined) {
        if (
          typeof window.requestIdleCallback === 'function' &&
          typeof window.cancelIdleCallback === 'function'
        ) {
          window.cancelIdleCallback(callbackId);
        } else {
          clearTimeout(callbackId);
        }
        this.#scheduledObserverCallbacks.delete(callbackId);
      }
      onCancel?.();
      this.#settleLoadContext(context);
    };

    callbackId = requestIdleCallback(() => {
      if (callbackId !== undefined) this.#scheduledObserverCallbacks.delete(callbackId);
      if (!releasePending()) return;
      context.runningObserverCallbacks += 1;
      this.#markContextActivity(context);

      try {
        if (
          !this.isConnected ||
          lifecycleGeneration !== this.#lifecycleGeneration ||
          observerEpoch !== this.#observerEpoch ||
          !this.#isLoadContextCurrent(context)
        ) {
          onCancel?.();
          return;
        }
        callback();
      } finally {
        context.runningObserverCallbacks = Math.max(0, context.runningObserverCallbacks - 1);
        context.activityEpoch += 1;
        this.#settleLoadContext(context);
      }
    });

    if (callbackId !== undefined) this.#scheduledObserverCallbacks.set(callbackId, { cancel });
    if (
      !this.#isLoadContextCurrent(context) ||
      observerEpoch !== this.#observerEpoch
    ) {
      cancel();
    }
  }

  #cancelScheduledObserverCallbacks() {
    for (const { cancel } of [...this.#scheduledObserverCallbacks.values()]) {
      cancel();
    }
    this.#scheduledObserverCallbacks.clear();
  }

  #disconnectInfinityScrollObserver() {
    this.#observerEpoch += 1;
    const context = this.#activeLoadContext;
    if (context && this.#isLoadContextCurrent(context)) {
      context.intersectingTargets.clear();
      context.immediateIntersection = false;
      this.#markContextActivity(context);
    }
    this.#cancelScheduledObserverCallbacks();
    this.infinityScrollObserver?.takeRecords?.();
    this.infinityScrollObserver?.disconnect();
    this.infinityScrollObserver = undefined;
    this.#observerOwner = undefined;
    const releaseInitialDelivery = this.#observerInitialRelease;
    this.#observerInitialRelease = undefined;
    releaseInitialDelivery?.();
  }

  /**
   * @param {{ id: string, origin: LoadContext['origin'], historyPolicy: 'write' | 'none' }} cause
   * @param {number} generation
   * @returns {LoadContext}
   */
  #createLoadContext(cause, generation) {
    const context = {
      ...cause,
      generation,
      lifecycleGeneration: this.#lifecycleGeneration,
      pendingPageTasks: 0,
      pendingInitialDeliveries: 0,
      pendingLifecycleCallbacks: 0,
      runningLifecycleCallbacks: 0,
      pendingObserverCallbacks: 0,
      runningObserverCallbacks: 0,
      activityEpoch: 0,
      quiescenceProbe: 0,
      immediateIntersection: false,
      intersectingTargets: new Set(),
      quiescent: false,
      active: true,
      navigationEpoch: this.#navigationEpoch,
      selectedEntry: { ...this.#selectedEntry },
      sourceCommitId: this.#renderedCommit.commitId,
      sourceRequestKey: this.#renderedCommit.requestKey,
      sourceDOMGeneration: this.#liveDOMCommit.generation,
      state: /** @type {'active'} */ ('active'),
      settlement: /** @type {'pending'} */ ('pending'),
    };
    this.#activeLoadContext = context;
    return context;
  }

  /**
   * @param {LoadContext} context
   * @returns {boolean}
   */
  #isLoadContextCurrent(context) {
    return (
      this.isConnected &&
      context.active &&
      this.#activeLoadContext === context &&
      context.generation === this.#requestGeneration &&
      context.lifecycleGeneration === this.#lifecycleGeneration &&
      context.navigationEpoch === this.#navigationEpoch &&
      context.state !== 'superseded' &&
      context.settlement !== 'superseded'
    );
  }

  /**
   * @param {LoadContext} context
   * @returns {boolean}
   */
  #hasPendingContextWork(context) {
    return (
      context.pendingPageTasks !== 0 ||
      context.pendingInitialDeliveries !== 0 ||
      context.pendingLifecycleCallbacks !== 0 ||
      context.runningLifecycleCallbacks !== 0 ||
      context.pendingObserverCallbacks !== 0 ||
      context.runningObserverCallbacks !== 0 ||
      context.immediateIntersection
    );
  }

  /**
   * @returns {boolean}
   */
  #hasPendingMutationWork() {
    return Boolean(
      this.#pendingNavigation ||
        (this.#activeServerRenderTransaction &&
          this.#activeServerRenderTransaction.settlement === 'pending') ||
        this.#liveDOMCommit.state === 'dirty'
    );
  }

  /**
   * Quiescence is a versioned CAS rather than a sticky boolean. Any resource scheduling,
   * callback start, boundary replacement, or observer replacement invalidates the probe.
   *
   * @param {LoadContext} context
   */
  #settleLoadContext(context) {
    if (
      !this.#isLoadContextCurrent(context) ||
      this.#hasPendingContextWork(context) ||
      this.#hasPendingMutationWork()
    ) {
      return;
    }

    const probe = ++context.quiescenceProbe;
    const activityEpoch = context.activityEpoch;
    const observerEpoch = this.#observerEpoch;
    const observer = this.infinityScrollObserver;
    const observerOwner = this.#observerOwner;
    const previousBoundary = this.refs.viewMorePrevious;
    const nextBoundary = this.refs.viewMoreNext;
    const domGeneration = this.#liveDOMCommit.generation;

    queueMicrotask(() => {
      if (
        !this.#isLoadContextCurrent(context) ||
        context.quiescenceProbe !== probe ||
        context.activityEpoch !== activityEpoch ||
        this.#observerEpoch !== observerEpoch ||
        this.infinityScrollObserver !== observer ||
        this.#observerOwner !== observerOwner ||
        this.refs.viewMorePrevious !== previousBoundary ||
        this.refs.viewMoreNext !== nextBoundary ||
        this.#liveDOMCommit.generation !== domGeneration ||
        this.#hasPendingContextWork(context) ||
        this.#hasPendingMutationWork()
      ) {
        return;
      }

      context.quiescent = true;
      context.state = 'quiescent';
      context.settlement = 'success';
    });
  }

  /**
   * @param {LoadContext} previousContext
   * @returns {LoadContext}
   */
  #promoteIndependentScrollContext(previousContext) {
    if (
      !this.#isLoadContextCurrent(previousContext) ||
      this.#hasDivergenceBarrier() ||
      !this.#isSelectedRenderedLiveCoherent()
    ) {
      return previousContext;
    }

    this.#disconnectInfinityScrollObserver();
    previousContext.settlement = 'success';
    this.#supersedeLoadContext(previousContext);
    const context = this.#createLoadContext(
      {
        id: `${this.sectionId}:scroll:${++this.#loadContextSequence}`,
        origin: 'scroll',
        historyPolicy: HISTORY_POLICY_WRITE,
      },
      previousContext.generation
    );
    this.#queueViewMoreObservation(context, false);
    return context;
  }

  /**
   * Captures immutable restore material for the last fully coherent section commit.
   * @returns {RollbackCapsule | undefined}
   */
  #captureRollbackCapsule() {
    if (!this.#isRenderedLiveCoherent() && this.#renderedCommit.requestKey) return;

    const root = this.closest('.shopify-section');
    if (!(root instanceof HTMLElement) || !root.id) return;

    const canonicalLink = document.querySelector('link[rel="canonical"]');
    return {
      rootId: root.id,
      sectionHTML: root.outerHTML,
      renderedCommit: {
        ...this.#renderedCommit,
        metadata: this.#renderedCommit.metadata
          ? { ...this.#renderedCommit.metadata }
          : undefined,
      },
      liveDOMCommit: { ...this.#liveDOMCommit },
      committedMetadata: this.#committedMetadata ? { ...this.#committedMetadata } : undefined,
      title: document.title,
      canonical: canonicalLink instanceof HTMLLinkElement ? canonicalLink.href : undefined,
    };
  }

  /**
   * Marks the live DOM dirty before a renderer can perform its first speculative mutation.
   * @param {ServerRenderTransaction} transaction
   */
  #markServerRenderMutation(transaction) {
    if (
      this.#activeServerRenderTransaction !== transaction ||
      transaction.domMutationLease.ownerToken !== transaction.token ||
      transaction.domMutationLease.navigationEpoch !== this.#navigationEpoch ||
      transaction.settlement !== 'pending'
    ) {
      return;
    }

    transaction.domMutationLease.state = 'dirty';
    transaction.domMutationLease.mutationToken = transaction.token;
    this.#rollbackCapsule = transaction.rollbackCapsule;
    this.#liveDOMCommit = {
      requestKey: transaction.requestURL,
      generation: transaction.generation,
      commitId: this.#renderedCommit.commitId,
      state: 'dirty',
      mutationToken: transaction.token,
    };
  }

  /**
   * Restores the complete committed tuple after a current failed target render.
   * @param {ServerRenderTransaction} transaction
   * @returns {boolean}
   */
  #restoreRollbackCapsule(transaction) {
    const lease = transaction.domMutationLease;
    const capsule = lease.rollbackCapsule;

    if (lease.state === 'clean') return this.#liveDOMCommit.state === 'committed';
    if (
      !capsule ||
      !this.isConnected ||
      this.#activeServerRenderTransaction !== transaction ||
      transaction.settlement !== 'pending' ||
      lease.ownerToken !== transaction.token ||
      lease.navigationEpoch !== this.#navigationEpoch ||
      this.#pendingNavigation?.transaction !== transaction
    ) {
      lease.state = 'invalid';
      return false;
    }

    const existingRoot = document.getElementById(capsule.rootId);
    const parsedRoot = new DOMParser()
      .parseFromString(capsule.sectionHTML, 'text/html')
      .getElementById(capsule.rootId);
    if (!(existingRoot instanceof HTMLElement) || !(parsedRoot instanceof HTMLElement)) {
      lease.state = 'invalid';
      return false;
    }

    morph(existingRoot, parsedRoot);
    if (
      this.#activeServerRenderTransaction !== transaction ||
      lease.ownerToken !== transaction.token ||
      lease.navigationEpoch !== this.#navigationEpoch
    ) {
      lease.state = 'invalid';
      return false;
    }

    super.updatedCallback();
    this.#renderedCommit = {
      ...capsule.renderedCommit,
      metadata: capsule.renderedCommit.metadata ? { ...capsule.renderedCommit.metadata } : undefined,
    };
    this.#liveDOMCommit = { ...capsule.liveDOMCommit, state: 'committed' };
    this.#committedMetadata = capsule.committedMetadata
      ? { ...capsule.committedMetadata }
      : undefined;
    document.title = capsule.title;
    const canonicalLink = document.querySelector('link[rel="canonical"]');
    if (canonicalLink instanceof HTMLLinkElement && capsule.canonical) {
      canonicalLink.href = capsule.canonical;
    }

    lease.state = 'rolled-back';
    this.#rollbackCapsule = undefined;
    return true;
  }

  /**
   * @param {Omit<ServerRenderTransaction, 'generation' | 'committed'>} transaction
   * @returns {ServerRenderTransaction}
   */
  #beginServerRenderTransaction(transaction) {
    transaction.recoveryContext = {
      id: `${transaction.token}:recovery`,
      origin: 'recovery',
      historyPolicy: HISTORY_POLICY_NONE,
    };

    const activeTransaction = this.#activeServerRenderTransaction;
    if (activeTransaction && activeTransaction !== transaction) {
      this.#supersedeServerRenderTransaction(activeTransaction);
    }

    const rollbackCapsule = this.#rollbackCapsule ?? this.#captureRollbackCapsule();
    transaction.rollbackCapsule = rollbackCapsule;
    transaction.sourceEntry = { ...this.#selectedEntry };
    transaction.targetEntry = { ...this.#selectedEntry };
    transaction.domMutationLease = {
      id: `${transaction.token}:dom:${this.#navigationEpoch}`,
      ownerToken: transaction.token,
      navigationEpoch: this.#navigationEpoch,
      state: this.#liveDOMCommit.state === 'dirty' ? 'dirty' : 'clean',
      rollbackCapsule,
      mutationToken: this.#liveDOMCommit.mutationToken,
    };

    this.#cancelScheduledCallbacks();
    const generation = this.#invalidateRequestGeneration();
    this.#disconnectInfinityScrollObserver();
    transaction.generation = generation;
    transaction.committed = false;
    transaction.settlement = 'pending';
    transaction.authority = createSectionRenderAuthority(transaction, {
      generation,
      selectedEntry: transaction.sourceEntry,
      instanceToken: this.#instanceToken,
      domMutationLeaseId: transaction.domMutationLease.id,
    });
    this.#activeServerRenderTransaction = transaction;
    return transaction;
  }

  /**
   * @param {LoadContext} context
   * @param {boolean} scheduleIdle
   */
  #queueViewMoreObservation(context, scheduleIdle = true) {
    if (!this.#isLoadContextCurrent(context)) return;

    this.#markContextActivity(context);
    context.pendingInitialDeliveries += 1;
    let released = false;
    const releaseInitialDelivery = () => {
      if (released) return;
      released = true;
      context.pendingInitialDeliveries = Math.max(0, context.pendingInitialDeliveries - 1);
      context.activityEpoch += 1;
      this.#settleLoadContext(context);
    };

    const scheduledObserverEpoch = this.#observerEpoch;
    const observe = () => {
      if (!this.#isLoadContextCurrent(context)) {
        releaseInitialDelivery();
        return;
      }

      if (!this.#observeViewMore(context, releaseInitialDelivery)) {
        releaseInitialDelivery();
      }
    };

    if (scheduleIdle) {
      this.#scheduleObserverCallback(
        scheduledObserverEpoch,
        context,
        observe,
        releaseInitialDelivery
      );
    } else {
      observe();
    }
  }

  /**
   * Captures retry authority only when a currently observed boundary resolves to the exact
   * failed browser-entry target. The mutable edge state remains owned by this observer generation.
   *
   * @param {LoadContext} context
   * @param {number} observerEpoch
   * @param {number} connectionEpoch
   * @param {HTMLElement | undefined} viewMorePrevious
   * @param {HTMLElement | undefined} viewMoreNext
   * @returns {ObserverRetryAuthority | undefined}
   */
  #captureObserverRetryAuthority(
    context,
    observerEpoch,
    connectionEpoch,
    viewMorePrevious,
    viewMoreNext
  ) {
    const failedTarget = this.#failedTarget;
    if (
      !failedTarget ||
      failedTarget.navigationEpoch !== this.#navigationEpoch ||
      !this.#isSelectedEntryCurrent(context.selectedEntry)
    ) {
      return;
    }

    for (const [direction, boundary] of /** @type {const} */ ([
      ['previous', viewMorePrevious],
      ['next', viewMoreNext],
    ])) {
      if (
        !(boundary instanceof HTMLElement) ||
        !boundary.isConnected ||
        boundary !==
          (direction === 'previous' ? this.refs.viewMorePrevious : this.refs.viewMoreNext)
      ) {
        continue;
      }

      const pageInfo = this.#getPage(direction);
      if (
        !pageInfo ||
        !this.#isPageRequestCurrent(pageInfo, context) ||
        !this.#isExactFailedTarget(pageInfo.cacheKey, context.selectedEntry)
      ) {
        continue;
      }

      return {
        boundary,
        direction,
        requestKey: pageInfo.cacheKey,
        contextKey: pageInfo.contextKey,
        observerEpoch,
        lifecycleGeneration: connectionEpoch,
        contextId: context.id,
        navigationEpoch: this.#navigationEpoch,
        selectedEntry: { ...context.selectedEntry },
        failedTarget,
        initialDeliveryConsumed: false,
        isIntersecting: undefined,
        accepted: false,
      };
    }
  }

  /**
   * Revalidates the complete observer/boundary/failed-entry authority immediately before retry work.
   *
   * @param {ObserverRetryAuthority} authority
   * @param {LoadContext} context
   * @param {PageInfo | undefined} [pageInfo]
   * @returns {boolean}
   */
  #isObserverRetryTargetCurrent(
    authority,
    context,
    pageInfo = this.#getPage(authority.direction)
  ) {
    const currentBoundary =
      authority.direction === 'previous'
        ? this.refs.viewMorePrevious
        : this.refs.viewMoreNext;
    return Boolean(
      pageInfo &&
        this.infinityScrollObserver &&
        this.#observerOwner?.retryAuthority === authority &&
        this.#observerOwner.epoch === authority.observerEpoch &&
        this.#observerOwner.contextId === authority.contextId &&
        this.#observerEpoch === authority.observerEpoch &&
        this.#lifecycleGeneration === authority.lifecycleGeneration &&
        authority.contextId === context.id &&
        authority.navigationEpoch === this.#navigationEpoch &&
        authority.failedTarget === this.#failedTarget &&
        currentBoundary === authority.boundary &&
        this.#isCurrentBoundaryTarget(authority.boundary) &&
        pageInfo.cacheKey === authority.requestKey &&
        pageInfo.contextKey === authority.contextKey &&
        this.#isPageRequestCurrent(pageInfo, context) &&
        this.#isSelectedEntryCurrent(authority.selectedEntry) &&
        authority.selectedEntry.navigationEpoch === context.selectedEntry.navigationEpoch &&
        authority.selectedEntry.requestKey === context.selectedEntry.requestKey &&
        authority.selectedEntry.url === context.selectedEntry.url &&
        isSameBrowserEntryAuthority(
          authority.selectedEntry.browserEntry,
          context.selectedEntry.browserEntry
        ) &&
        this.#isExactFailedTarget(pageInfo.cacheKey, context.selectedEntry)
    );
  }

  /**
   * Consumes the first current delivery exactly once, then accepts only a later rising edge.
   *
   * @param {ObserverRetryAuthority} authority
   * @param {LoadContext} context
   * @param {{ isIntersecting: boolean }} entry
   * @param {boolean} initialDelivery
   * @returns {boolean}
   */
  #acceptObserverRetryEntry(authority, context, entry, initialDelivery) {
    if (!this.#isObserverRetryTargetCurrent(authority, context)) return false;

    if (initialDelivery) {
      if (!authority.initialDeliveryConsumed) {
        authority.initialDeliveryConsumed = true;
        authority.isIntersecting = entry.isIntersecting;
      }
      return false;
    }

    if (!authority.initialDeliveryConsumed) return false;

    const wasIntersecting = authority.isIntersecting;
    authority.isIntersecting = entry.isIntersecting;
    if (
      authority.accepted ||
      !entry.isIntersecting ||
      wasIntersecting !== false ||
      !this.#isObserverRetryTargetCurrent(authority, context)
    ) {
      return false;
    }

    authority.accepted = true;
    return true;
  }

  /**
   * A failed accepted retry gets a new observer generation and therefore a new suppressed
   * initial delivery before another edge can be accepted.
   *
   * @param {ObserverRetryAuthority} authority
   * @param {LoadContext} context
   * @param {PageInfo} pageInfo
   */
  #restartObserverAfterRetryFailure(authority, context, pageInfo) {
    if (
      pageInfo.cacheKey !== authority.requestKey ||
      !this.#isObserverRetryTargetCurrent(authority, context)
    ) {
      return;
    }
    this.#queueViewMoreObservation(context, false);
  }

  /**
   * @param {LoadContext} context
   * @param {() => void} completeInitialDelivery
   * @returns {boolean}
   */
  #observeViewMore(context, completeInitialDelivery) {
    if (!this.#isLoadContextCurrent(context)) return false;

    const { viewMorePrevious, viewMoreNext } = this.refs;

    this.#disconnectInfinityScrollObserver();

    if (!viewMorePrevious && !viewMoreNext) return false;

    const observerEpoch = this.#observerEpoch;
    const connectionEpoch = this.#lifecycleGeneration;
    const retryAuthority = this.#captureObserverRetryAuthority(
      context,
      observerEpoch,
      connectionEpoch,
      viewMorePrevious,
      viewMoreNext
    );
    const observerOwner = Object.freeze({
      token: Symbol(`${this.sectionId}:observer:${observerEpoch}`),
      epoch: observerEpoch,
      contextId: context.id,
      retryAuthority,
    });
    const initialTargets = new Set();
    let initialDeliveryComplete = false;
    const releaseObserverInitialDelivery = () => {
      if (initialDeliveryComplete) return;
      initialDeliveryComplete = true;
      if (this.#observerInitialRelease === releaseObserverInitialDelivery) {
        this.#observerInitialRelease = undefined;
      }
      completeInitialDelivery();
    };
    this.#observerInitialRelease = releaseObserverInitialDelivery;

    const observer = new IntersectionObserver(
      async (entries) => {
        if (
          !this.#isObserverCallbackCurrent(
            observer,
            observerOwner,
            observerEpoch,
            connectionEpoch,
            context
          )
        ) {
          return;
        }

        const wasQuiescent = context.quiescent && context.state === 'quiescent';
        context.runningObserverCallbacks += 1;
        this.#markContextActivity(context);

        try {
          const capturedEntries = entries.map((entry) => ({
            target: entry.target,
            isIntersecting: entry.isIntersecting,
          }));

          if (viewTransition.current) await Promise.resolve(viewTransition.current).catch(() => {});
          if (
            !this.#isObserverCallbackCurrent(
              observer,
              observerOwner,
              observerEpoch,
              connectionEpoch,
              context
            )
          ) {
            return;
          }

          const currentEntries = capturedEntries
            .filter((entry) => this.#isCurrentBoundaryTarget(entry.target))
            .map((entry) => ({
              target: entry.target,
              isIntersecting: entry.isIntersecting,
              initialDelivery: initialTargets.delete(entry.target),
              retryScoped: false,
              retryAccepted: false,
            }));

          for (const entry of currentEntries) {
            if (entry.isIntersecting) {
              context.intersectingTargets.add(entry.target);
            } else {
              context.intersectingTargets.delete(entry.target);
            }
          }
          for (const target of [...context.intersectingTargets]) {
            if (!this.#isCurrentBoundaryTarget(target)) context.intersectingTargets.delete(target);
          }
          for (const entry of currentEntries) {
            if (
              retryAuthority?.boundary === entry.target &&
              this.#isObserverRetryTargetCurrent(retryAuthority, context)
            ) {
              entry.retryScoped = true;
              entry.retryAccepted = this.#acceptObserverRetryEntry(
                retryAuthority,
                context,
                entry,
                entry.initialDelivery
              );
            }
          }
          const retryScopeCurrent = Boolean(
            retryAuthority && this.#isObserverRetryTargetCurrent(retryAuthority, context)
          );
          const hasIntersection = [...context.intersectingTargets].some(
            (target) =>
              !(
                retryScopeCurrent &&
                retryAuthority?.boundary === target &&
                !retryAuthority.accepted
              )
          );
          context.immediateIntersection = hasIntersection;
          const callbackContext =
            context.historyPolicy === HISTORY_POLICY_NONE && wasQuiescent && hasIntersection
              ? this.#promoteIndependentScrollContext(context)
              : context;
          if (callbackContext !== context) return;
          if (!this.#isLoadContextCurrent(callbackContext)) return;

          for (const entry of currentEntries) {
            if (entry.retryScoped) {
              if (
                !entry.retryAccepted ||
                !retryAuthority ||
                !this.#isObserverRetryTargetCurrent(retryAuthority, callbackContext)
              ) {
                continue;
              }

              if (retryAuthority.direction === 'previous') {
                this.#startPageTask(
                  () => this.#renderPreviousPage(callbackContext, retryAuthority),
                  callbackContext
                );
              } else {
                this.#startPageTask(
                  () => this.#renderNextPage(callbackContext, retryAuthority),
                  callbackContext
                );
              }
              continue;
            }

            if (!entry.isIntersecting) continue;

            const { viewMorePrevious, viewMoreNext } = this.refs;

            if (entry.target === viewMorePrevious && viewMorePrevious.isConnected) {
              this.#startPageTask(
                () => this.#renderPreviousPage(callbackContext),
                callbackContext
              );
            } else if (entry.target === viewMoreNext && viewMoreNext.isConnected) {
              this.#startPageTask(() => this.#renderNextPage(callbackContext), callbackContext);
            }
          }

          const completesInitialDelivery = !initialDeliveryComplete && initialTargets.size === 0;
          if (completesInitialDelivery) releaseObserverInitialDelivery();
        } finally {
          context.runningObserverCallbacks = Math.max(0, context.runningObserverCallbacks - 1);
          context.activityEpoch += 1;
          this.#settleLoadContext(context);
        }
      },
      {
        rootMargin: '100px',
      }
    );
    this.infinityScrollObserver = observer;
    this.#observerOwner = observerOwner;

    let observed = false;

    if (
      viewMorePrevious?.isConnected &&
      viewMorePrevious === this.refs.viewMorePrevious &&
      this.#isLoadContextCurrent(context)
    ) {
      initialTargets.add(viewMorePrevious);
      observer.observe(viewMorePrevious);
      observed = true;
    }

    if (
      viewMoreNext?.isConnected &&
      viewMoreNext === this.refs.viewMoreNext &&
      this.#isLoadContextCurrent(context)
    ) {
      initialTargets.add(viewMoreNext);
      observer.observe(viewMoreNext);
      observed = true;
    }

    if (!observed) releaseObserverInitialDelivery();
    return observed;
  }

  /**
   * Side-effect-free guard for queued or resumed observer callbacks.
   * @param {IntersectionObserver} observer
   * @param {ObserverOwner} observerOwner
   * @param {number} observerEpoch
   * @param {number} connectionEpoch
   * @param {LoadContext} context
   * @returns {boolean}
   */
  #isObserverCallbackCurrent(observer, observerOwner, observerEpoch, connectionEpoch, context) {
    return (
      this.isConnected &&
      this.infinityScrollObserver === observer &&
      this.#observerOwner === observerOwner &&
      observerOwner.epoch === observerEpoch &&
      observerOwner.contextId === context.id &&
      this.#observerEpoch === observerEpoch &&
      this.#lifecycleGeneration === connectionEpoch &&
      this.#isLoadContextCurrent(context)
    );
  }

  /**
   * @param {Element} target
   * @returns {boolean}
   */
  #isCurrentBoundaryTarget(target) {
    const { viewMorePrevious, viewMoreNext } = this.refs;
    return (
      target.isConnected &&
      this.contains(target) &&
      (target === viewMorePrevious || target === viewMoreNext)
    );
  }

  /**
   * @param {PageInfo | undefined} pageInfo
   * @returns {boolean}
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage)) return false;

    return true;
  }

  /**
   * @param {PageInfo} pageInfo
   * @param {LoadContext} context
   * @returns {boolean}
   */
  #isPageRequestCurrent(pageInfo, context) {
    return (
      this.#isLoadContextCurrent(context) &&
      this.#isSelectedEntryCurrent(context.selectedEntry) &&
      pageInfo.contextKey === getPageContextKey(context.selectedEntry.url)
    );
  }

  /**
   * @param {"previous" | "next"} type
   * @param {LoadContext} context
   * @returns {Promise<boolean>}
   */
  async #fetchPage(type, context) {
    const pageInfo = this.#getPage(type);

    if (
      !pageInfo ||
      !this.#shouldUsePage(pageInfo) ||
      !this.#isPageRequestCurrent(pageInfo, context) ||
      this.#isExactFailedTarget(pageInfo.cacheKey, context.selectedEntry)
    ) {
      return false;
    }

    return this.#fetchSpecificPage(pageInfo, context, false);
  }

  /**
   * @param {PageInfo} pageInfo
   * @param {LoadContext} context
   * @param {boolean} [explicitRetry]
   * @param {ObserverRetryAuthority} [retryAuthority]
   * @returns {Promise<boolean>}
   */
  async #fetchSpecificPage(pageInfo, context, explicitRetry = false, retryAuthority) {
    if (!this.#shouldUsePage(pageInfo) || !this.#isPageRequestCurrent(pageInfo, context)) return false;
    const authorizedRetry = Boolean(
      explicitRetry &&
        retryAuthority &&
        pageInfo.cacheKey === retryAuthority.requestKey &&
        this.#isObserverRetryTargetCurrent(retryAuthority, context) &&
        this.#isExactFailedTarget(pageInfo.cacheKey, context.selectedEntry)
    );
    const refreshesMalformedCachedPage = Boolean(
      explicitRetry && !retryAuthority && this.pages.has(pageInfo.cacheKey)
    );
    const fresh = authorizedRetry || refreshesMalformedCachedPage;
    if (retryAuthority && !authorizedRetry) return false;
    if (!explicitRetry && this.#isExactFailedTarget(pageInfo.cacheKey, context.selectedEntry)) return false;
    if (!fresh && this.pages.has(pageInfo.cacheKey)) return true;

    const existingRequest = this.#pendingPageRequests.get(pageInfo.cacheKey);
    if (
      !fresh &&
      existingRequest?.generation === context.generation &&
      existingRequest.contextId === context.id
    ) {
      return existingRequest.promise;
    }

    const promise = this.#requestPage(pageInfo, context, fresh);
    this.#pendingPageRequests.set(pageInfo.cacheKey, {
      generation: context.generation,
      contextId: context.id,
      promise,
    });

    try {
      return await promise;
    } finally {
      const currentRequest = this.#pendingPageRequests.get(pageInfo.cacheKey);
      if (currentRequest?.promise === promise) {
        this.#pendingPageRequests.delete(pageInfo.cacheKey);
      }
    }
  }

  /**
   * @param {PageInfo} pageInfo
   * @param {LoadContext} context
   * @param {boolean} fresh
   * @returns {Promise<boolean>}
   */
  async #requestPage(pageInfo, context, fresh) {
    try {
      const requestURL = new URL(pageInfo.requestURL.toString());
      const pageContent = await sectionRenderer.getSectionHTML(
        this.sectionId,
        true,
        requestURL,
        fresh ? { fresh: true } : undefined
      );

      if (!this.#isPageRequestCurrent(pageInfo, context)) return false;

      this.pages.set(pageInfo.cacheKey, pageContent);
      return true;
    } catch (error) {
      if (isAbortError(error)) return false;
      throw error;
    }
  }

  /**
   * @param {LoadContext} context
   * @param {ObserverRetryAuthority} [retryAuthority]
   */
  async #renderNextPage(context, retryAuthority) {
    const { grid } = this.refs;
    if (!grid) return;

    const nextPage = this.#getPage('next');
    if (!nextPage || !this.#shouldUsePage(nextPage) || !this.#isPageRequestCurrent(nextPage, context)) return;
    if (retryAuthority && !this.#isObserverRetryTargetCurrent(retryAuthority, context)) return;
    const renderKey = `${context.id}:${nextPage.cacheKey}`;
    if (this.#renderingPageKeys.has(renderKey)) return;

    this.#renderingPageKeys.add(renderKey);

    try {
      let nextPageData = this.#getPageData(nextPage);

      if (retryAuthority || !nextPageData) {
        let fetched;
        try {
          fetched = await this.#fetchSpecificPage(
            nextPage,
            context,
            Boolean(retryAuthority) || this.pages.has(nextPage.cacheKey),
            retryAuthority
          );
        } catch (error) {
          if (retryAuthority) {
            this.#restartObserverAfterRetryFailure(retryAuthority, context, nextPage);
          }
          throw error;
        }
        if (!this.#isPageRequestCurrent(nextPage, context)) return;
        if (retryAuthority && !this.#isObserverRetryTargetCurrent(retryAuthority, context)) return;
        if (!fetched) {
          if (retryAuthority) {
            this.#restartObserverAfterRetryFailure(retryAuthority, context, nextPage);
          }
          return;
        }

        nextPageData = this.#getPageData(nextPage);
        if (!nextPageData) {
          if (retryAuthority) {
            this.#restartObserverAfterRetryFailure(retryAuthority, context, nextPage);
          }
          return;
        }
      }

      if (!this.#isPageRequestCurrent(nextPage, context)) return;
      if (retryAuthority && !this.#isObserverRetryTargetCurrent(retryAuthority, context)) return;
      grid.append(...nextPageData.items);

      this.#aspectRatioHelper?.processNewElements();

      if (!this.#isPageRequestCurrent(nextPage, context)) return;
      const committed = this.#pushPageState(
        nextPage,
        nextPageData.metadata,
        context,
        retryAuthority
      );
      if (retryAuthority && !committed) return;

      if (!this.#isPageRequestCurrent(nextPage, context)) return;
      this.#updateBoundaryLink('next', nextPageData.nextLink, context);

      this.#scheduleLifecycleCallback(context, () => {
        if (!this.#isLoadContextCurrent(context)) return;
        this.#startPageTask(() => this.#fetchPage('next', context), context);
      });
    } finally {
      this.#renderingPageKeys.delete(renderKey);
    }
  }

  /**
   * @param {LoadContext} context
   * @param {ObserverRetryAuthority} [retryAuthority]
   */
  async #renderPreviousPage(context, retryAuthority) {
    const { grid } = this.refs;
    if (!grid) return;

    const previousPage = this.#getPage('previous');
    if (
      !previousPage ||
      !this.#shouldUsePage(previousPage) ||
      !this.#isPageRequestCurrent(previousPage, context)
    ) {
      return;
    }
    if (retryAuthority && !this.#isObserverRetryTargetCurrent(retryAuthority, context)) return;
    const renderKey = `${context.id}:${previousPage.cacheKey}`;
    if (this.#renderingPageKeys.has(renderKey)) return;

    this.#renderingPageKeys.add(renderKey);

    try {
      let previousPageData = this.#getPageData(previousPage);

      if (retryAuthority || !previousPageData) {
        let fetched;
        try {
          fetched = await this.#fetchSpecificPage(
            previousPage,
            context,
            Boolean(retryAuthority) || this.pages.has(previousPage.cacheKey),
            retryAuthority
          );
        } catch (error) {
          if (retryAuthority) {
            this.#restartObserverAfterRetryFailure(retryAuthority, context, previousPage);
          }
          throw error;
        }
        if (!this.#isPageRequestCurrent(previousPage, context)) return;
        if (retryAuthority && !this.#isObserverRetryTargetCurrent(retryAuthority, context)) return;
        if (!fetched) {
          if (retryAuthority) {
            this.#restartObserverAfterRetryFailure(retryAuthority, context, previousPage);
          }
          return;
        }

        previousPageData = this.#getPageData(previousPage);
        if (!previousPageData) {
          if (retryAuthority) {
            this.#restartObserverAfterRetryFailure(retryAuthority, context, previousPage);
          }
          return;
        }
      }

      const scrollTop = window.scrollY;
      const firstElement = grid.firstElementChild;
      const oldHeight = firstElement ? firstElement.getBoundingClientRect().top + window.scrollY : 0;

      if (!this.#isPageRequestCurrent(previousPage, context)) return;
      if (retryAuthority && !this.#isObserverRetryTargetCurrent(retryAuthority, context)) return;
      grid.prepend(...previousPageData.items);

      this.#aspectRatioHelper?.processNewElements();

      if (!this.#isPageRequestCurrent(previousPage, context)) return;
      const committed = this.#pushPageState(
        previousPage,
        previousPageData.metadata,
        context,
        retryAuthority
      );
      if (retryAuthority && !committed) return;

      if (!this.#isPageRequestCurrent(previousPage, context)) return;
      const focusTarget = this.#updateBoundaryLink(
        'previous',
        previousPageData.previousLink,
        context,
        false
      );

      if (firstElement) {
        const newHeight = firstElement.getBoundingClientRect().top + window.scrollY;
        const heightDiff = newHeight - oldHeight;
        window.scrollTo({
          top: scrollTop + heightDiff,
          behavior: 'instant',
        });
      }

      this.#restoreLogicalFocus(focusTarget);

      this.#scheduleLifecycleCallback(context, () => {
        if (!this.#isLoadContextCurrent(context)) return;
        this.#startPageTask(() => this.#fetchPage('previous', context), context);
      });
    } finally {
      this.#renderingPageKeys.delete(renderKey);
    }
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {PageInfo | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === 'previous';

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];
    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    if (!Number.isFinite(currentCardPage)) return;

    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;
    const boundaryMarker = isPrevious ? this.refs.viewMorePrevious : this.refs.viewMoreNext;
    if (!(boundaryMarker instanceof HTMLElement)) return;

    let historyURL;

    if (boundaryMarker instanceof HTMLAnchorElement) {
      historyURL = normalizeHistoryURL(boundaryMarker.href);
      historyURL.hash = window.location.hash;
    } else {
      historyURL = normalizeHistoryURL(window.location.href);
      historyURL.searchParams.set('page', page.toString());
      historyURL.searchParams.sort();
    }

    const requestURL = normalizeRequestURL(historyURL);

    return {
      page,
      historyURL,
      requestURL,
      cacheKey: getPageCacheKey(requestURL),
      contextKey: getPageContextKey(requestURL),
    };
  }

  /**
   * @param {PageInfo} pageInfo
   * @returns {{
   *   items: NodeListOf<Element>,
   *   metadata?: { title: string, canonical: string },
   *   previousLink?: HTMLAnchorElement,
   *   nextLink?: HTMLAnchorElement
   * } | undefined}
   */
  #getPageData(pageInfo) {
    const pageHTML = this.pages.get(pageInfo.cacheKey);
    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, 'text/html');
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;

    const metadataElement = parsedPage.querySelector('[ref="pageMetadata"]');
    const metadata =
      metadataElement instanceof HTMLElement ? this.#parsePageMetadata(metadataElement) : undefined;
    const previousLink = parsedPage.querySelector('[ref="viewMorePrevious"]');
    const nextLink = parsedPage.querySelector('[ref="viewMoreNext"]');

    return {
      items: gridElement.querySelectorAll(':scope > [ref="cards[]"]'),
      metadata,
      previousLink: previousLink instanceof HTMLAnchorElement ? previousLink : undefined,
      nextLink: nextLink instanceof HTMLAnchorElement ? nextLink : undefined,
    };
  }

  /**
   * @param {"previous" | "next"} type
   * @param {HTMLAnchorElement | undefined} sourceLink
   * @param {LoadContext} context
   * @param {boolean} [restoreFocus]
   * @returns {HTMLElement | undefined}
   */
  #updateBoundaryLink(type, sourceLink, context, restoreFocus = true) {
    const { grid, viewMorePrevious, viewMoreNext } = this.refs;
    if (!grid) return;

    const currentMarker = type === 'previous' ? viewMorePrevious : viewMoreNext;
    const markerWasFocused = document.activeElement === currentMarker;
    this.#disconnectInfinityScrollObserver();

    if (!(currentMarker instanceof HTMLAnchorElement)) {
      this.#queueViewMoreObservation(context);
      return;
    }

    /** @type {HTMLElement | undefined} */
    let focusTarget;

    if (sourceLink) {
      const replacementLink = /** @type {HTMLAnchorElement} */ (sourceLink.cloneNode(true));
      const replacementURL = normalizeHistoryURL(replacementLink.href);
      replacementURL.hash = window.location.hash;
      replacementLink.href = replacementURL.toString();
      currentMarker.replaceWith(replacementLink);
      if (markerWasFocused) focusTarget = replacementLink;
    } else {
      currentMarker.remove();
      if (markerWasFocused) focusTarget = this.#getLoadedListBoundaryFocusTarget(type, grid);
    }

    if (restoreFocus) this.#restoreLogicalFocus(focusTarget);
    this.#queueViewMoreObservation(context);
    return focusTarget;
  }

  /**
   * Returns a meaningful focus target at the newly loaded edge of the list.
   * @param {"previous" | "next"} type
   * @param {HTMLUListElement} grid
   * @returns {HTMLElement}
   */
  #getLoadedListBoundaryFocusTarget(type, grid) {
    const cards = grid.querySelectorAll(':scope > [ref="cards[]"]');
    const boundaryCard = cards[type === 'previous' ? 0 : cards.length - 1];
    const focusableSelector =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
    const interactiveElement =
      boundaryCard instanceof HTMLElement
        ? boundaryCard.matches(focusableSelector)
          ? boundaryCard
          : boundaryCard.querySelector(focusableSelector)
        : null;

    if (interactiveElement instanceof HTMLElement) return interactiveElement;

    return boundaryCard instanceof HTMLElement ? boundaryCard : grid;
  }

  /**
   * @param {HTMLElement | undefined} focusTarget
   */
  #restoreLogicalFocus(focusTarget) {
    if (!focusTarget?.isConnected) return;

    const focusableSelector =
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]';
    const needsTemporaryTabIndex =
      !focusTarget.matches(focusableSelector) && !focusTarget.hasAttribute('tabindex');

    if (needsTemporaryTabIndex) focusTarget.setAttribute('tabindex', '-1');
    focusTarget.focus({ preventScroll: true });
    if (needsTemporaryTabIndex) focusTarget.removeAttribute('tabindex');

    if (!focusTarget.isConnected || document.activeElement !== focusTarget) return;

    const rect = focusTarget.getBoundingClientRect();
    const isVisible =
      rect.bottom > 0 &&
      rect.right > 0 &&
      rect.top < window.innerHeight &&
      rect.left < window.innerWidth;

    if (!isVisible) {
      focusTarget.scrollIntoView({ block: 'nearest' });
    }
  }

  /**
   * @param {HTMLElement} metadataElement
   * @returns {{ title: string, canonical: string } | undefined}
   */
  #parsePageMetadata(metadataElement) {
    const { title, canonical } = metadataElement.dataset;
    if (typeof title !== 'string' || typeof canonical !== 'string') return;
    return { title, canonical };
  }

  /**
   * @param {PageInfo} pageInfo
   * @param {{ title: string, canonical: string } | undefined} metadata
   * @param {LoadContext} context
   * @param {ObserverRetryAuthority} [retryAuthority]
   * @returns {boolean}
   */
  #pushPageState(pageInfo, metadata, context, retryAuthority) {
    if (!this.#canPublishPageState(pageInfo, context, retryAuthority)) return false;

    const wasFailedTarget = this.#isExactFailedTarget(pageInfo.cacheKey, context.selectedEntry);
    const currentURL = normalizeHistoryURL(window.location.href).toString();
    const targetURL = pageInfo.historyURL.toString();
    const reconcilesCurrentEntry = currentURL === targetURL;

    if (!this.#canPublishPageState(pageInfo, context, retryAuthority)) return false;
    if (!reconcilesCurrentEntry) {
      history.pushState(history.state, '', targetURL);
    }
    setCurrentBrowserEntryMetadata({
      owner: this.sectionId,
      pageMetadata: metadata,
    });
    if (
      !this.#isLoadContextCurrent(context) ||
      normalizeHistoryURL(window.location.href).toString() !== targetURL ||
      (retryAuthority &&
        !this.#isObserverRetryTargetCurrent(retryAuthority, context, pageInfo))
    ) {
      return false;
    }

    this.#selectedEntry = this.#captureSelectedEntry(history.state, this.#navigationEpoch);
    const commitId = ++this.#domCommitSequence;
    this.#renderedCommit = {
      requestKey: pageInfo.cacheKey,
      generation: context.generation,
      commitId,
      metadata: metadata ? { ...metadata } : undefined,
    };
    this.#liveDOMCommit = {
      requestKey: pageInfo.cacheKey,
      generation: context.generation,
      commitId,
      state: 'committed',
    };
    if (wasFailedTarget || this.#selectedEntry.requestKey === pageInfo.cacheKey) {
      this.#failedTarget = undefined;
    }
    this.#rollbackCapsule = undefined;
    context.selectedEntry = { ...this.#selectedEntry };
    context.sourceCommitId = commitId;
    context.sourceRequestKey = pageInfo.cacheKey;
    context.sourceDOMGeneration = context.generation;
    this.#commitPageMetadata(metadata, context);
    return true;
  }

  /**
   * Enforces the exact-entry/DOM/source-commit divergence barrier immediately before history.
   * @param {PageInfo} pageInfo
   * @param {LoadContext} context
   * @param {ObserverRetryAuthority} [retryAuthority]
   * @returns {boolean}
   */
  #canPublishPageState(pageInfo, context, retryAuthority) {
    const authorizedRecoveryRetry = Boolean(
      retryAuthority &&
        this.#isObserverRetryTargetCurrent(retryAuthority, context, pageInfo)
    );
    return (
      this.#isPageRequestCurrent(pageInfo, context) &&
      (context.historyPolicy === HISTORY_POLICY_WRITE || authorizedRecoveryRetry) &&
      context.navigationEpoch === this.#navigationEpoch &&
      this.#isSelectedEntryCurrent(context.selectedEntry) &&
      !this.#pendingNavigation &&
      (authorizedRecoveryRetry || !this.#hasDivergenceBarrier()) &&
      (authorizedRecoveryRetry
        ? this.#isRenderedLiveCoherent()
        : this.#isSelectedRenderedLiveCoherent()) &&
      context.sourceCommitId === this.#renderedCommit.commitId &&
      context.sourceRequestKey === this.#renderedCommit.requestKey &&
      context.sourceDOMGeneration === this.#liveDOMCommit.generation &&
      this.#isCurrentBoundaryForPage(pageInfo)
    );
  }

  /**
   * @param {PageInfo} pageInfo
   * @returns {boolean}
   */
  #isCurrentBoundaryForPage(pageInfo) {
    const { grid, viewMorePrevious, viewMoreNext } = this.refs;
    return Boolean(
      grid?.isConnected &&
        this.contains(grid) &&
        pageInfo.contextKey === getPageContextKey(this.#selectedEntry.url) &&
        (!viewMorePrevious || (viewMorePrevious.isConnected && this.contains(viewMorePrevious))) &&
        (!viewMoreNext || (viewMoreNext.isConnected && this.contains(viewMoreNext)))
    );
  }

  /**
   * Atomically updates the committed metadata source of truth after a guarded pagination write.
   *
   * @param {{ title: string, canonical: string } | undefined} metadata
   * @param {LoadContext} context
   */
  #commitPageMetadata(metadata, context) {
    if (
      !this.#isLoadContextCurrent(context) ||
      !this.#isSelectedRenderedLiveCoherent() ||
      context.sourceCommitId !== this.#renderedCommit.commitId
    ) {
      return;
    }

    this.#committedMetadata = metadata ? { ...metadata } : undefined;
    this.#renderedCommit.metadata = metadata ? { ...metadata } : undefined;

    const metadataElement = this.querySelector('[ref="pageMetadata"]');
    if (metadataElement instanceof HTMLElement) {
      if (metadata) {
        metadataElement.dataset.title = metadata.title;
        metadataElement.dataset.canonical = metadata.canonical;
      } else {
        delete metadataElement.dataset.title;
        delete metadataElement.dataset.canonical;
      }
    }

    if (metadata) applyPageMetadata(metadata);
  }

  /**
   * Reads canonical server metadata after a full morph and optionally initializes history state.
   *
   * @param {LoadContext} context
   * @param {boolean} replaceHistory
   * @param {{
   *   selectedEntry: SelectedEntry,
   *   requestKey: string,
   *   metadata?: { title: string, canonical: string }
   * }} [renderedTarget]
   */
  #syncCurrentPageMetadata(context, replaceHistory, renderedTarget) {
    if (!this.#isLoadContextCurrent(context)) return;

    const metadataElement = this.querySelector('[ref="pageMetadata"]');
    const metadata =
      renderedTarget?.metadata ??
      (metadataElement instanceof HTMLElement ? this.#parsePageMetadata(metadataElement) : undefined);
    if (!this.#isLoadContextCurrent(context)) return;
    if (
      renderedTarget &&
      (!this.#isSelectedEntryCurrent(renderedTarget.selectedEntry) ||
        renderedTarget.requestKey !== renderedTarget.selectedEntry.requestKey)
    ) {
      return;
    }

    this.#committedMetadata = metadata ? { ...metadata } : undefined;
    this.#renderedCommit = {
      requestKey: renderedTarget?.requestKey ?? getPageCacheKey(window.location.href),
      generation: context.generation,
      commitId: ++this.#domCommitSequence,
      metadata: metadata ? { ...metadata } : undefined,
    };
    this.#liveDOMCommit = {
      requestKey: this.#renderedCommit.requestKey,
      generation: context.generation,
      commitId: this.#renderedCommit.commitId,
      state: 'committed',
    };
    context.sourceCommitId = this.#renderedCommit.commitId;
    context.sourceRequestKey = this.#renderedCommit.requestKey;
    context.sourceDOMGeneration = this.#liveDOMCommit.generation;
    this.#rollbackCapsule = undefined;

    if (replaceHistory && context.historyPolicy === HISTORY_POLICY_WRITE) {
      if (!this.#isLoadContextCurrent(context)) return;
      history.replaceState(
        history.state,
        '',
        normalizeHistoryURL(window.location.href)
      );
      setCurrentBrowserEntryMetadata({
        owner: this.sectionId,
        pageMetadata: metadata,
      });
      if (!this.#isLoadContextCurrent(context)) return;
      this.#selectedEntry = this.#captureSelectedEntry(history.state, this.#navigationEpoch);
      context.selectedEntry = { ...this.#selectedEntry };
    } else if (renderedTarget) {
      this.#selectedEntry = { ...renderedTarget.selectedEntry };
      context.selectedEntry = { ...renderedTarget.selectedEntry };
      setCurrentBrowserEntryMetadata({
        owner: this.sectionId,
        pageMetadata: metadata,
      });
    }

    if (
      this.#selectedEntry.requestKey === this.#renderedCommit.requestKey &&
      this.#liveDOMCommit.requestKey === this.#renderedCommit.requestKey
    ) {
      const failedTarget = this.#failedTarget;
      if (
        !failedTarget ||
        this.#isExactFailedTarget(this.#renderedCommit.requestKey, this.#selectedEntry) ||
        !isSameBrowserEntryAuthority(
          failedTarget.selectedEntry.browserEntry,
          this.#selectedEntry.browserEntry
        )
      ) {
        this.#failedTarget = undefined;
      }
    }

    if (
      metadata &&
      this.#isLoadContextCurrent(context) &&
      this.#isSelectedRenderedLiveCoherent()
    ) {
      applyPageMetadata(metadata);
    }
  }

  #invalidateRequestGeneration() {
    if (this.#activeLoadContext) {
      this.#supersedeLoadContext(this.#activeLoadContext);
    }
    this.#requestGeneration += 1;
    this.pages.clear();
    this.#pendingPageRequests.clear();
    this.#renderingPageKeys.clear();
    return this.#requestGeneration;
  }

  get sectionId() {
    const id = this.getAttribute('section-id');

    if (!id) throw new Error('The section-id attribute is required');

    return id;
  }

  /**
   * Allows Facets to use a same-target fast path only for a fully committed exact browser entry.
   *
   * @param {string} requestKey
   * @param {string} historyURL
   * @returns {boolean}
   */
  isFacetTargetCoherent(requestKey, historyURL) {
    return (
      this.isConnected &&
      getPageCacheKey(historyURL) === requestKey &&
      this.#isSelectedEntryCurrent() &&
      this.#selectedEntry.requestKey === requestKey &&
      this.#renderedCommit.requestKey === requestKey &&
      this.#liveDOMCommit.requestKey === requestKey &&
      !this.#activeServerRenderTransaction &&
      !this.#pendingNavigation &&
      !this.#failedTarget &&
      !this.#rollbackCapsule &&
      this.#isSelectedRenderedLiveCoherent()
    );
  }

  /**
   * @param {unknown} state
   * @param {number} navigationEpoch
   * @returns {SelectedEntry}
   */
  #captureSelectedEntry(state, navigationEpoch) {
    const url = window.location.href;
    return {
      navigationEpoch,
      url,
      state,
      browserEntry: captureBrowserEntryAuthority(),
      requestKey: getPageCacheKey(url),
    };
  }

  /**
   * @param {SelectedEntry} [entry]
   * @returns {boolean}
   */
  #isSelectedEntryCurrent(entry = this.#selectedEntry) {
    const currentBrowserEntry = captureBrowserEntryAuthority();
    return (
      entry.navigationEpoch === this.#navigationEpoch &&
      entry.navigationEpoch === browserNavigationEpoch &&
      entry.url === window.location.href &&
      isSameBrowserEntryAuthority(entry.browserEntry, currentBrowserEntry) &&
      isSameBrowserEntryAuthority(entry.browserEntry, this.#selectedEntry.browserEntry)
    );
  }

  /**
   * @returns {boolean}
   */
  #isSelectedRenderedLiveCoherent() {
    return (
      this.#selectedEntry.requestKey === this.#renderedCommit.requestKey &&
      this.#isRenderedLiveCoherent()
    );
  }

  /**
   * @returns {boolean}
   */
  #isRenderedLiveCoherent() {
    return (
      this.#liveDOMCommit.state === 'committed' &&
      this.#liveDOMCommit.requestKey === this.#renderedCommit.requestKey &&
      this.#liveDOMCommit.commitId === this.#renderedCommit.commitId &&
      this.#liveDOMCommit.generation === this.#renderedCommit.generation
    );
  }

  /**
   * @param {string} requestKey
   * @param {SelectedEntry} entry
   * @returns {boolean}
   */
  #isExactFailedTarget(requestKey, entry) {
    const failedTarget = this.#failedTarget;
    return Boolean(
      failedTarget &&
        failedTarget.requestKey === requestKey &&
        failedTarget.navigationEpoch === this.#navigationEpoch &&
        failedTarget.selectedEntry.requestKey === entry.requestKey &&
        isSameBrowserEntryAuthority(
          failedTarget.selectedEntry.browserEntry,
          entry.browserEntry
        )
    );
  }

  /**
   * @returns {boolean}
   */
  #hasDivergenceBarrier() {
    return Boolean(
      this.#pendingNavigation ||
        (this.#failedTarget?.navigationEpoch === this.#navigationEpoch) ||
        !this.#isSelectedRenderedLiveCoherent()
    );
  }

  /**
   * @param {{ title?: unknown, canonical?: unknown } | undefined} metadata
   * @returns {{ title: string, canonical: string } | undefined}
   */
  #normalizeSelectedMetadata(metadata) {
    if (
      !metadata ||
      typeof metadata.title !== 'string' ||
      typeof metadata.canonical !== 'string'
    ) {
      return;
    }
    return { title: metadata.title, canonical: metadata.canonical };
  }

  /**
   * Atomically rebinds the complete pagination chain to a no-history browser traversal.
   * @returns {boolean}
   */
  #adoptPaginationChainForPopstate() {
    const context = this.#activeLoadContext;
    if (
      !context ||
      !context.active ||
      context.generation !== this.#requestGeneration ||
      context.lifecycleGeneration !== this.#lifecycleGeneration
    ) {
      return false;
    }

    context.navigationEpoch = this.#navigationEpoch;
    context.selectedEntry = { ...this.#selectedEntry };
    context.origin = 'popstate';
    context.historyPolicy = HISTORY_POLICY_NONE;
    this.#markContextActivity(context);

    this.#disconnectInfinityScrollObserver();
    this.#markContextActivity(context);

    if (this.refs.viewMorePrevious || this.refs.viewMoreNext) {
      this.#queueViewMoreObservation(context, false);
    } else {
      this.#settleLoadContext(context);
    }
    return true;
  }

  /**
   * Restores speculative DOM before its owner is superseded by a browser-entry change.
   * The temporary pending-navigation binding gives the exact dirty lease sole rollback authority.
   *
   * @param {ServerRenderTransaction} transaction
   * @param {PopStateEvent} event
   * @param {number} navigationEpoch
   * @returns {boolean}
   */
  #rollbackServerRenderForNavigation(transaction, event, navigationEpoch) {
    if (transaction.domMutationLease?.state !== 'dirty') return false;

    transaction.domMutationLease.navigationEpoch = navigationEpoch;
    this.#pendingNavigation = {
      transaction,
      selectedEntry: this.#selectedEntry,
      stagedMetadata: this.#normalizeSelectedMetadata(getPopStatePageMetadata(event)),
    };
    return this.#restoreRollbackCapsule(transaction);
  }

  /**
   * @param {ServerRenderTransaction} transaction
   */
  #supersedeServerRenderTransaction(transaction) {
    if (this.#activeServerRenderTransaction !== transaction) return;

    transaction.preparedRender?.abort();
    if (transaction.domMutationLease?.state === 'dirty') {
      this.#rollbackCapsule = transaction.domMutationLease.rollbackCapsule;
      this.#liveDOMCommit = {
        requestKey: transaction.requestURL,
        generation: transaction.generation,
        commitId: this.#renderedCommit.commitId,
        state: 'dirty',
        mutationToken: transaction.token,
      };
    }

    supersedeSectionRenderAuthority(transaction.authority);
    transaction.settlement = 'superseded';
    transaction.committed = true;
    this.#activeServerRenderTransaction = undefined;
    if (this.#pendingNavigation?.transaction === transaction) this.#pendingNavigation = undefined;

    if (transaction.facetOwned) {
      document.dispatchEvent(
        new CustomEvent(FACET_RENDER_SUPERSEDE_EVENT, {
          detail: {
            sectionId: transaction.sectionId,
            token: transaction.token,
          },
        })
      );
    }
  }

  /**
   * Suppresses write-capable work in non-owning lists during the same browser traversal.
   * @param {PopStateEvent} event
   * @param {number} navigationEpoch
   */
  suppressHistoryForNavigation(event, navigationEpoch) {
    if (!this.isConnected) return;

    const navigationRollback = this.#rollbackCapsule ?? this.#captureRollbackCapsule();
    this.#navigationEpoch = navigationEpoch;
    this.#selectedEntry = this.#captureSelectedEntry(event.state, navigationEpoch);
    this.#rollbackCapsule ??= navigationRollback;

    const activeTransaction = this.#activeServerRenderTransaction;
    if (activeTransaction) {
      this.#rollbackServerRenderForNavigation(activeTransaction, event, navigationEpoch);
      this.#supersedeServerRenderTransaction(activeTransaction);
      super.updatedCallback();
      this.#initializeRenderedState({
        cause: activeTransaction.recoveryContext,
        replaceHistory: false,
        preserveRenderedCommit: true,
      });
      return;
    }

    this.#adoptPaginationChainForPopstate();
  }

  /**
   * Restores the complete server-rendered section for a browser history entry.
   * @param {PopStateEvent} event
   * @param {number} [navigationEpoch]
   */
  restoreHistoryState(event, navigationEpoch = ++browserNavigationEpoch) {
    if (!this.isConnected) return;

    const navigationRollback = this.#rollbackCapsule ?? this.#captureRollbackCapsule();
    this.#navigationEpoch = navigationEpoch;
    this.#selectedEntry = this.#captureSelectedEntry(event.state, navigationEpoch);
    this.#rollbackCapsule ??= navigationRollback;

    const requestURL = normalizeRequestURL(window.location.href);
    const requestURLKey = getPageCacheKey(requestURL);
    const activeTransaction = this.#activeServerRenderTransaction;
    let supersededTransactionMorphed = false;

    if (activeTransaction?.requestURL === requestURLKey) {
      if (!adoptSectionRenderAuthority(activeTransaction.authority, this.#selectedEntry)) {
        const rolledBack = this.#rollbackServerRenderForNavigation(
          activeTransaction,
          event,
          navigationEpoch
        );
        supersededTransactionMorphed = activeTransaction.morphed === true && !rolledBack;
        this.#supersedeServerRenderTransaction(activeTransaction);
      } else {
        this.#pendingNavigation = {
          transaction: activeTransaction,
          selectedEntry: this.#selectedEntry,
          stagedMetadata: this.#normalizeSelectedMetadata(getPopStatePageMetadata(event)),
        };
        return;
      }
    } else if (activeTransaction) {
      const rolledBack = this.#rollbackServerRenderForNavigation(
        activeTransaction,
        event,
        navigationEpoch
      );
      supersededTransactionMorphed = activeTransaction.morphed === true && !rolledBack;
      this.#supersedeServerRenderTransaction(activeTransaction);
    }

    if (
      requestURLKey === this.#renderedCommit.requestKey &&
      this.#isRenderedLiveCoherent() &&
      !supersededTransactionMorphed
    ) {
      this.#failedTarget = undefined;
      this.#rollbackCapsule = undefined;
      this.#adoptPaginationChainForPopstate();
      this.#applySelectedHistoryMetadata(getPopStatePageMetadata(event));
      return;
    }

    const transaction = this.#beginServerRenderTransaction({
      token: `${this.sectionId}:history:${++this.#historyRenderSequence}`,
      sectionId: this.sectionId,
      requestURL: requestURLKey,
      historyURL: this.#selectedEntry.url,
      origin: 'popstate',
      historyPolicy: HISTORY_POLICY_NONE,
      source: 'network',
      publishHistory: false,
      historyPublished: false,
      settlement: 'pending',
      facetOwned: false,
    });
    this.#pendingNavigation = {
      transaction,
      selectedEntry: this.#selectedEntry,
      stagedMetadata: this.#normalizeSelectedMetadata(getPopStatePageMetadata(event)),
    };

    this.#startPageTask(() => this.#restoreServerHistoryState(transaction, requestURL), undefined);
  }

  /**
   * @param {ServerRenderTransaction} transaction
   * @param {URL} requestURL
   */
  async #restoreServerHistoryState(transaction, requestURL) {
    const fresh = this.#isExactFailedTarget(transaction.requestURL, transaction.targetEntry);
    const render = sectionRenderer.createSectionRender(transaction.sectionId, {
      cache: true,
      fresh,
      url: new URL(requestURL.toString()),
    });
    transaction.preparedRender = render;

    try {
      const prepared = await render.ready;
      if (!this.#isServerRenderTransactionCurrent(transaction)) {
        render.abort();
        if (this.#activeServerRenderTransaction === transaction) {
          this.#recoverFailedServerRender(transaction, 'superseded');
        }
        return;
      }

      this.#markServerRenderMutation(transaction);
      if (!this.#isServerRenderTransactionCurrent(transaction)) {
        render.abort();
        if (this.#activeServerRenderTransaction === transaction) {
          this.#recoverFailedServerRender(transaction, 'superseded');
        }
        return;
      }

      const outcome = await render.commit();
      transaction.morphed = outcome.status === 'success';
      if (!this.#isServerRenderTransactionCurrent(transaction)) {
        if (this.#activeServerRenderTransaction === transaction) {
          this.#recoverFailedServerRender(transaction, 'superseded');
        }
        return;
      }
      if (outcome.status !== 'success') {
        this.#recoverFailedServerRender(transaction);
        return;
      }

      transaction.source = outcome.source ?? prepared.source;
      this.#commitServerRenderTransaction(transaction);
    } catch (error) {
      const isCurrent = this.#isServerRenderTransactionCurrent(transaction);
      render.abort();
      if (!isCurrent) {
        if (this.#activeServerRenderTransaction === transaction) {
          this.#recoverFailedServerRender(transaction, 'superseded');
        }
        return;
      }

      this.#recoverFailedServerRender(transaction);
      if (!isAbortError(error)) throw error;
    }
  }

  /**
   * @param {ServerRenderTransaction} transaction
   * @returns {boolean}
   */
  #isServerRenderTransactionCurrent(transaction) {
    const commonCurrent =
      this.isConnected &&
      this.#activeServerRenderTransaction === transaction &&
      !transaction.committed &&
      transaction.settlement === 'pending' &&
      transaction.generation === this.#requestGeneration &&
      transaction.sectionId === this.sectionId &&
      transaction.domMutationLease.ownerToken === transaction.token &&
      transaction.domMutationLease.navigationEpoch === this.#navigationEpoch &&
      transaction.domMutationLease.state !== 'invalid';
    if (!commonCurrent) return false;

    if (transaction.historyPublished) {
      const committedEntry = transaction.committedEntry;
      return Boolean(
        committedEntry &&
          this.#isSelectedEntryCurrent(committedEntry) &&
          transaction.requestURL === committedEntry.requestKey
      );
    }

    return (
      transaction.authority.snapshot.generation === transaction.generation &&
      transaction.authority.snapshot.instanceToken === this.#instanceToken &&
      transaction.authority.snapshot.domMutationLeaseId === transaction.domMutationLease.id &&
      isSectionRenderAuthorityCurrent(transaction.authority) &&
      transaction.authority.snapshot.navigationEpoch === this.#navigationEpoch &&
      (transaction.origin === 'filter' ||
        transaction.requestURL === this.#selectedEntry.requestKey)
    );
  }

  /**
   * @param {{ title?: unknown, canonical?: unknown } | undefined} metadata
   */
  #applySelectedHistoryMetadata(metadata) {
    const selectedMetadata = this.#normalizeSelectedMetadata(metadata) ?? this.#committedMetadata;
    if (
      !selectedMetadata ||
      !this.#isSelectedEntryCurrent() ||
      !this.#isSelectedRenderedLiveCoherent() ||
      this.#pendingNavigation
    ) {
      return;
    }

    this.#committedMetadata = { ...selectedMetadata };
    this.#renderedCommit.metadata = { ...selectedMetadata };
    const metadataElement = this.querySelector('[ref="pageMetadata"]');
    if (metadataElement instanceof HTMLElement) {
      metadataElement.dataset.title = selectedMetadata.title;
      metadataElement.dataset.canonical = selectedMetadata.canonical;
    }
    applyPageMetadata(selectedMetadata);
  }

  /**
   * Publishes the successful filter URL and complete pagination metadata in one history write.
   *
   * @param {ServerRenderTransaction} transaction
   */
  #publishServerRenderHistory(transaction) {
    if (
      transaction.historyPolicy !== HISTORY_POLICY_WRITE ||
      !transaction.publishHistory ||
      transaction.historyPublished
    ) {
      return this.#isServerRenderTransactionCurrent(transaction);
    }
    if (typeof transaction.historyURL !== 'string') return false;

    const metadataElement = this.querySelector('[ref="pageMetadata"]');
    const metadata =
      metadataElement instanceof HTMLElement ? this.#parsePageMetadata(metadataElement) : undefined;
    if (
      !this.#isServerRenderTransactionCurrent(transaction) ||
      !this.#isSelectedEntryCurrent() ||
      this.#pendingNavigation?.transaction !== transaction ||
      transaction.domMutationLease.state !== 'dirty' ||
      transaction.domMutationLease.mutationToken !== transaction.token ||
      this.#liveDOMCommit.state !== 'dirty' ||
      this.#liveDOMCommit.mutationToken !== transaction.token
    ) {
      return false;
    }
    history.pushState(history.state, '', transaction.historyURL);
    setCurrentBrowserEntryMetadata({
      owner: this.sectionId,
      pageMetadata: metadata,
      urlParameters: transaction.urlParameters,
    });

    transaction.historyPublished = true;
    this.#selectedEntry = this.#captureSelectedEntry(history.state, this.#navigationEpoch);
    transaction.committedEntry = { ...this.#selectedEntry };
    transaction.targetEntry = { ...this.#selectedEntry };
    if (this.#pendingNavigation?.transaction === transaction) {
      this.#pendingNavigation.selectedEntry = { ...this.#selectedEntry };
      this.#pendingNavigation.stagedMetadata = metadata ? { ...metadata } : undefined;
    }
    return this.#isServerRenderTransactionCurrent(transaction);
  }

  /**
   * Applies refs, metadata, prefetching, and observation once after a confirmed morph.
   * @param {ServerRenderTransaction} transaction
   */
  #commitServerRenderTransaction(transaction) {
    if (!this.#isServerRenderTransactionCurrent(transaction)) return;

    super.updatedCallback();
    if (!this.#publishServerRenderHistory(transaction)) {
      this.#recoverFailedServerRender(transaction);
      return;
    }
    if (!this.#isServerRenderTransactionCurrent(transaction)) {
      this.#recoverFailedServerRender(transaction, 'superseded');
      return;
    }

    const metadataElement = this.querySelector('[ref="pageMetadata"]');
    const metadata =
      metadataElement instanceof HTMLElement ? this.#parsePageMetadata(metadataElement) : undefined;
    const committedEntry = transaction.committedEntry ?? this.#selectedEntry;
    if (!this.#isSelectedEntryCurrent(committedEntry)) {
      this.#recoverFailedServerRender(transaction, 'superseded');
      return;
    }

    transaction.committed = true;
    transaction.settlement = 'success';
    transaction.domMutationLease.state = 'committed';
    settleSectionRenderAuthority(transaction.authority, 'success');
    this.#activeServerRenderTransaction = undefined;
    if (this.#pendingNavigation?.transaction === transaction) this.#pendingNavigation = undefined;
    this.#initializeRenderedState({
      cause: {
        id: transaction.token,
        origin: transaction.origin,
        historyPolicy: transaction.historyPolicy,
      },
      replaceHistory: false,
      renderedTarget: {
        selectedEntry: { ...committedEntry },
        requestKey: transaction.requestURL,
        metadata,
      },
    });
  }

  /**
   * Restores the last committed DOM lifecycle after a current failure without publishing history.
   *
   * @param {ServerRenderTransaction} transaction
   * @param {'failure' | 'superseded'} [settlement]
   */
  #recoverFailedServerRender(transaction, settlement = 'failure') {
    if (this.#activeServerRenderTransaction !== transaction) return;

    if (!this.#isSelectedEntryCurrent()) {
      this.#selectedEntry = this.#captureSelectedEntry(history.state, this.#navigationEpoch);
    }
    const pendingNavigation =
      this.#pendingNavigation?.transaction === transaction ? this.#pendingNavigation : undefined;
    const restored = this.#restoreRollbackCapsule(transaction);
    if (settlement === 'failure' && transaction.origin === 'popstate') {
      const failedEntry = pendingNavigation?.selectedEntry ?? this.#selectedEntry;
      this.#failedTarget = {
        selectedEntry: { ...failedEntry },
        navigationEpoch: this.#navigationEpoch,
        requestKey: transaction.requestURL,
      };
    }

    transaction.committed = true;
    transaction.settlement = settlement;
    settleSectionRenderAuthority(transaction.authority, settlement);
    this.#activeServerRenderTransaction = undefined;
    if (this.#pendingNavigation?.transaction === transaction) this.#pendingNavigation = undefined;
    if (!this.isConnected) return;

    if (
      restored &&
      this.#selectedEntry.requestKey === this.#renderedCommit.requestKey &&
      this.#isRenderedLiveCoherent()
    ) {
      this.#failedTarget = undefined;
    }

    if (!restored) super.updatedCallback();
    this.#initializeRenderedState({
      cause: transaction.recoveryContext,
      replaceHistory: false,
      preserveRenderedCommit: true,
    });
  }

  /** @param {CustomEvent} event */
  #handleServerRenderSettled = (event) => {
    const detail = event.detail;
    const transaction = this.#activeServerRenderTransaction;

    if (
      !transaction ||
      typeof detail !== 'object' ||
      detail === null ||
      detail.token !== transaction.token ||
      detail.sectionId !== transaction.sectionId ||
      detail.requestURL !== transaction.requestURL ||
      !['success', 'failure', 'superseded'].includes(detail.status) ||
      (detail.source !== 'cache' && detail.source !== 'network')
    ) {
      return;
    }

    transaction.source = detail.source;
    if (detail.status === 'success') {
      this.#commitServerRenderTransaction(transaction);
    } else {
      this.#recoverFailedServerRender(
        transaction,
        detail.status === 'superseded' ? 'superseded' : 'failure'
      );
    }
  };

  /** @param {CustomEvent} event */
  #handleServerDomMutation = (event) => {
    const detail = event.detail;
    const transaction = this.#activeServerRenderTransaction;
    if (
      !transaction ||
      typeof detail !== 'object' ||
      detail === null ||
      detail.sectionId !== transaction.sectionId ||
      detail.token !== transaction.token ||
      (detail.phase !== 'started' && detail.phase !== 'morphed')
    ) {
      return;
    }

    this.#markServerRenderMutation(transaction);
  };

  /** @param {CustomEvent} event */
  #handleFilterUpdate = (event) => {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;

    const renderTransaction = event.detail?.renderTransaction;
    if (
      typeof renderTransaction !== 'object' ||
      renderTransaction === null ||
      typeof renderTransaction.token !== 'string' ||
      renderTransaction.sectionId !== this.sectionId ||
      typeof renderTransaction.requestURL !== 'string' ||
      typeof renderTransaction.historyURL !== 'string' ||
      renderTransaction.origin !== 'filter' ||
      renderTransaction.historyPolicy !== HISTORY_POLICY_WRITE
    ) {
      return;
    }

    renderTransaction.source = 'network';
    renderTransaction.facetOwned = true;
    renderTransaction.settlement = 'pending';
    const transaction = this.#beginServerRenderTransaction(renderTransaction);
    const targetURL = normalizeHistoryURL(transaction.historyURL ?? window.location.href).toString();
    const targetState = history.state;
    transaction.targetEntry = {
      navigationEpoch: this.#navigationEpoch,
      url: targetURL,
      state: targetState,
      browserEntry: transaction.sourceEntry.browserEntry,
      requestKey: transaction.requestURL,
    };
    this.#pendingNavigation = {
      transaction,
      selectedEntry: transaction.targetEntry,
    };
  };
}
